import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { enableWebPush, currentSubscription, isPushSupported } from "../lib/webpush";
import { usePwaInstall } from "../lib/usePwaInstall";
import { fmtDate, fmtDateTime } from "../lib/formatters";
import {
  LogOut, User, FileText, Wrench, CalendarClock, Briefcase, MessageSquare, AlertOctagon,
  Image as ImgIcon, Star, Send, Trash2, Upload, CheckCircle2, XCircle,
  MapPin, Clock, UserCheck, Bell, ChevronLeft, ChevronRight, Sparkles, Download,
  ArrowRight, BookOpen, Phone, ShieldAlert, AlertTriangle, Youtube, ClipboardCheck, Plus,
  Globe, Cloud, Coins, PhoneCall, Syringe, Landmark, Smartphone, FileUp, ShieldCheck,
  PartyPopper, Camera, Drama, Mountain, UtensilsCrossed, HeartPulse, Leaf, Handshake,
  GraduationCap, ShoppingBag, CheckCircle, ChevronDown, ChevronUp,
} from "lucide-react";
import { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import ProjectOverviewPanel from "../components/ProjectOverviewPanel";
import FirstLoginNotifPrompt from "../components/FirstLoginNotifPrompt";
import ParticipantFeedbackCard from "../components/ParticipantFeedbackCard";
import ImgWithFallback from "../components/ImgWithFallback";

// USD is the single currency shown across the participant portal — all
// activities and services are priced in USD per company policy.
const USD = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const TABS_ALL = [
  { key: "dashboard", label: "Home", icon: User },
  { key: "cform", label: "C-Form", icon: FileText, requires: "india" },
  { key: "general", label: "General Info", icon: FileText, requires: "non-india" },
  { key: "documents", label: "Documents", icon: FileUp },
  { key: "destination", label: "Destination", icon: Globe },
  { key: "services", label: "Services", icon: Wrench },
  { key: "activities", label: "Activities", icon: CalendarClock },
  { key: "projects", label: "Project Feedback", icon: Briefcase },
  { key: "instructions", label: "Pre-arrival", icon: BookOpen },
  { key: "videos", label: "Videos", icon: Youtube },
  { key: "attendance", label: "Attendance", icon: ClipboardCheck },
  { key: "feedback", label: "Feedback", icon: Star },
  { key: "contacts", label: "Contacts", icon: Phone },
  { key: "suggestions", label: "Suggestions", icon: MessageSquare },
  { key: "disclaimer", label: "Consent", icon: ImgIcon },
];
// C-Form is India-only under the Foreigners Act — foreign-destination participants
// see a lighter "General Information" tab instead. `isIndian` may be undefined
// while /participant/me is still loading; keep the C-Form visible in that window.
const tabsForPlacement = (isIndian) => TABS_ALL.filter(t => {
  if (!t.requires) return true;
  if (t.requires === "india") return isIndian !== false;
  if (t.requires === "non-india") return isIndian === false;
  return true;
});

// ---------------- Sub-panels ----------------
function PlacementPreferencePanel({ initial }) {
  const [editing, setEditing] = useState(!initial);
  const [form, setForm] = useState({
    country: initial?.country || "",
    city: initial?.city || "",
    placement_area: initial?.placement_area || "",
    custom_area: initial?.custom_area || "",
    specialisation: initial?.specialisation || "",
  });
  const [saved, setSaved] = useState(initial || null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.country) { toast.error("Pick a destination country"); return; }
    if (!form.city.trim()) { toast.error("Enter your destination city"); return; }
    if (!form.placement_area) { toast.error("Pick a placement area"); return; }
    if (form.placement_area === "Other" && !form.custom_area.trim()) { toast.error("Please specify your custom area"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/participant/placement-preference", form);
      toast.success("Placement preference shared with your coordinator");
      setSaved(data);
      setEditing(false);
      // Notify sibling CuratedToursPanel to reload for the new country/city.
      window.dispatchEvent(new CustomEvent("aii:placement_saved"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!editing && saved) {
    return (
      <div className="aii-card p-5" data-testid="placement-pref-summary">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1"><Sparkles size={11}/> Suggestions &amp; comments</div>
            <h3 className="font-display font-semibold text-lg">Shape your journey</h3>
          </div>
          <button onClick={() => setEditing(true)} data-testid="placement-pref-edit" className="text-xs text-brand hover:underline">Edit</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="flex justify-between border-b border-[color:var(--aii-border)] pb-1"><span className="text-[color:var(--aii-muted)]">Country</span><span className="font-medium">{saved.country}</span></div>
          <div className="flex justify-between border-b border-[color:var(--aii-border)] pb-1"><span className="text-[color:var(--aii-muted)]">City</span><span className="font-medium">{saved.city}</span></div>
          <div className="flex justify-between border-b border-[color:var(--aii-border)] pb-1"><span className="text-[color:var(--aii-muted)]">Placement suggestion</span><span className="font-medium">{saved.placement_area === "Other" ? saved.custom_area : saved.placement_area}</span></div>
          <div className="flex justify-between border-b border-[color:var(--aii-border)] pb-1"><span className="text-[color:var(--aii-muted)]">Specialisation / comment</span><span className="font-medium">{saved.specialisation || <em className="text-[color:var(--aii-muted)] font-normal">—</em>}</span></div>
        </div>
      </div>
    );
  }

  return (
    <div className="aii-card p-5 border-brand/40" data-testid="placement-pref-form">
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1"><Sparkles size={11}/> Placement preference</div>
        <h3 className="font-display font-semibold text-lg">Shape your journey</h3>
        <p className="text-xs text-[color:var(--aii-muted)] mt-1">Please submit this <b>before your arrival</b> so we can fix your placement according to your choice.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Destination country</label>
          <select data-testid="pp-country" value={form.country} onChange={e=>setForm({...form, country:e.target.value})} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
            <option value="">Choose…</option>
            {DESTINATION_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Destination city</label>
          <input data-testid="pp-city" value={form.city} onChange={e=>setForm({...form, city:e.target.value})} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" placeholder="e.g. Jaipur"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Placement area</label>
          <select data-testid="pp-area" value={form.placement_area} onChange={e=>setForm({...form, placement_area:e.target.value})} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
            <option value="">Choose…</option>
            {PLACEMENT_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        {form.placement_area === "Other" && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Specify your area</label>
            <input data-testid="pp-custom" value={form.custom_area} onChange={e=>setForm({...form, custom_area:e.target.value})} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" placeholder="Tell us your placement idea"/>
          </div>
        )}
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Specialisation or interest</label>
          <input data-testid="pp-spec" value={form.specialisation} onChange={e=>setForm({...form, specialisation:e.target.value})} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" placeholder="e.g. Emergency medicine, ceramics, kindergarten age group…"/>
          <div className="mt-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1.5" data-testid="pp-spec-note">
            <AlertTriangle size={11} className="flex-shrink-0 mt-0.5"/>
            <span><b>Note:</b> Specialisation requests cannot be guaranteed — we&rsquo;ll do our best to align you with the closest match on the ground.</span>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        {saved && <button onClick={() => setEditing(false)} className="px-3 py-2 text-sm">Cancel</button>}
        <button data-testid="pp-save" onClick={save} disabled={busy} className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5">
          <CheckCircle2 size={14}/> {busy ? "Saving…" : "Submit preference"}
        </button>
      </div>
    </div>
  );
}

function CuratedToursPanel() {
  const [state, setState] = useState({ tours: [], using_preference: false, country: "", city: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/country-tours"); setState(data); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const onSaved = () => load();
    window.addEventListener("aii:placement_saved", onSaved);
    return () => window.removeEventListener("aii:placement_saved", onSaved);
  }, []);

  const inquire = async (t) => {
    setBusy(t.id);
    try {
      const { data } = await api.post(`/country-tours/${t.id}/inquire`);
      if (data.already) toast.success("You've already inquired about this one");
      else toast.success(`Interest noted! ${data.count} traveller(s) inquired so far.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  if (loading) return null;
  if (!state.using_preference && state.tours.length === 0) return null;

  return (
    <div className="aii-card p-5" data-testid="curated-tours-panel">
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1"><MapPin size={11}/> Curated for {state.city || "your destination"}, {state.country}</div>
        <h3 className="font-display font-semibold text-lg">Tours we can organise</h3>
        <p className="text-xs text-[color:var(--aii-muted)] mt-1">Inquire to signal interest — we&rsquo;ll fix dates once we have enough travellers on board.</p>
      </div>
      {state.tours.length === 0 ? (
        <div className="text-center py-8 text-sm text-[color:var(--aii-muted)]">
          <MapPin size={28} className="mx-auto opacity-40 mb-2"/>
          Nothing curated for {state.city}, {state.country} yet — we&rsquo;ll drop tours here as our BDOM designs them.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {state.tours.map((t) => (
            <div key={t.id} className="border border-[color:var(--aii-border)] rounded-lg overflow-hidden flex flex-col" data-testid={`tour-card-${t.id}`}>
              {t.image_url ? (
                <div className="aspect-[16/9] bg-slate-100 overflow-hidden cursor-zoom-in"
                     onClick={() => setLightbox({ items: [{ type: "image", src: t.image_url }], index: 0 })}
                     data-testid={`tour-img-${t.id}`}>
                  <img src={t.image_url} alt={t.title} className="w-full h-full object-cover"/>
                </div>
              ) : (
                <div className="aspect-[16/9] bg-gradient-to-br from-[color:var(--aii-green-tint)] to-slate-100 flex items-center justify-center text-[color:var(--aii-muted)]"><MapPin size={28}/></div>
              )}
              <div className="p-4 flex-1 flex flex-col">
                <div className="font-display font-semibold text-base leading-tight">{t.title}</div>
                {t.description && <p className="text-xs text-[color:var(--aii-text-2)] mt-1 line-clamp-3">{t.description}</p>}
                <div className="flex items-center gap-3 text-[11px] mt-2 flex-wrap">
                  {t.duration && <span className="flex items-center gap-1 text-[color:var(--aii-muted)]"><Clock size={10}/> {t.duration}</span>}
                  <span className="font-semibold text-brand">{USD(t.price)}</span>
                  {t.tags?.length > 0 && <span className="text-[color:var(--aii-muted)]">{t.tags.join(" · ")}</span>}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] mt-2">Min {t.min_pax} pax · {t.inquiry_count || 0} interested</div>
                <button onClick={() => inquire(t)} disabled={busy === t.id || t.my_inquired} data-testid={`tour-inquire-${t.id}`}
                  className={`mt-3 w-full py-2 rounded-md font-semibold text-sm transition ${t.my_inquired ? "bg-emerald-600 text-white" : "bg-brand text-white hover:bg-brand-dark"} disabled:opacity-70`}>
                  {t.my_inquired ? <><CheckCircle2 size={13} className="inline mr-1"/> Interest noted</> : (busy === t.id ? "Sending…" : "I'm interested")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {lightbox && (
        <Lightbox items={lightbox.items} index={lightbox.index}
                  onClose={() => setLightbox(null)}
                  onNav={(dir) => setLightbox(l => l ? { ...l, index: (l.index + dir + l.items.length) % l.items.length } : l)}/>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// UpcomingActivitiesPanel + UpcomingTripsPanel
// ----------------------------------------------------------------------
// Approved activities / trips created by staff (Activity Planner / Trip
// Organizer) surface here as "book or skip" cards. Same UX shape as
// CuratedToursPanel above so the participant portal feels consistent.
// ----------------------------------------------------------------------
// Extract a YouTube video ID from any of the common URL shapes so we can
// embed the thumbnail preview even when the founder pastes a full share link.
function ytIdFrom(url) {
  // Module-scope helper — filter Boolean + join with " · ".  Kept at module
  // scope so we don't recreate the closure on every card render.
  return _ytIdFrom(url);
}
function locationLabel(parts) {
  return (parts || []).filter(Boolean).join(" · ");
}
function _ytIdFrom(url) {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : "";
}

// -------------------------------------------------------------
// Category → lucide icon map. Keeps the chip row scanable on
// mobile without emoji (design guideline: prefer lucide-react).
// Falls back to Sparkles when we don't have a bespoke icon.
// -------------------------------------------------------------
const CAT_ICON = {
  Cultural: Drama,
  Adventure: Mountain,
  Culinary: UtensilsCrossed,
  Wellness: HeartPulse,
  Nature: Leaf,
  Volunteering: Handshake,
  Nightlife: PartyPopper,
  Learning: GraduationCap,
  Shopping: ShoppingBag,
  Other: Sparkles,
};

// -------------------------------------------------------------
// mediaFromItem — normalises an activity/tour into an ordered list
// of media items ({type, src}) so the Lightbox can flip through
// videos, YouTube trailers AND stills as one continuous slideshow.
// -------------------------------------------------------------
function mediaFromItem(item) {
  const media = [];
  if (item?.video_url) media.push({ type: "video", src: item.video_url });
  const yt = ytIdFrom(item?.youtube_url);
  if (yt) media.push({ type: "youtube", src: yt });
  (item?.images || []).forEach((u) => media.push({ type: "image", src: u }));
  return media;
}

// Build the {items, index} payload the Lightbox expects, honouring an
// optional imgIdx so we can open the media viewer starting on the same
// still the user tapped in a thumbnail grid.
function openItemMedia(item, imgIdx = 0) {
  const items = mediaFromItem(item);
  const src = (item?.images || [])[imgIdx];
  let idx = 0;
  if (src) {
    const found = items.findIndex((m) => m.type === "image" && m.src === src);
    if (found >= 0) idx = found;
  }
  return { items, index: idx };
}

function BookSkipCard({ item, kind, busy, onDecide, onOpen, onImageOpen, currencySymbol }) {
  const decided = item.my_decision;
  const hero = item.images?.[0];
  const ytId = ytIdFrom(item.youtube_url);
  const openIfNotBusy = (ev) => {
    // Ignore clicks that originate from the Book/Skip buttons — they have their
    // own handlers and stopPropagation. We open the details modal on card click.
    if (ev.target.closest('[data-noopen]')) return;
    onOpen?.(item);
  };
  const heroClick = (ev) => {
    // Tapping the image opens the Lightbox with the full media stack (video +
    // YouTube + all images). Falls back to opening the details modal when
    // there's nothing to preview.
    if (!onImageOpen || mediaFromItem(item).length === 0) { openIfNotBusy(ev); return; }
    ev.stopPropagation();
    onImageOpen(item, 0);
  };
  return (
    <div className="border border-[color:var(--aii-border)] rounded-lg overflow-hidden flex flex-col cursor-pointer hover:shadow-md transition-shadow"
      onClick={openIfNotBusy}
      data-testid={`${kind}-card-${item.id}`}>
      {/* Media header */}
      {hero ? (
        <div className="aspect-[16/9] bg-slate-100 overflow-hidden" onClick={heroClick} data-testid={`${kind}-hero-${item.id}`}>
          <ImgWithFallback src={hero} alt={item.name} className="w-full h-full object-cover"
                           fallbackLabel="Activity photo unavailable"/>
        </div>
      ) : item.video_url ? (
        <div className="aspect-[16/9] bg-black cursor-zoom-in" onClick={heroClick} data-testid={`${kind}-hero-${item.id}`}>
          <video src={item.video_url} className="w-full h-full object-cover pointer-events-none"/>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg"><div className="w-0 h-0 border-l-[14px] border-l-brand border-y-[9px] border-y-transparent ml-1"/></div>
          </div>
        </div>
      ) : ytId ? (
        <div className="aspect-[16/9] block relative bg-black overflow-hidden group cursor-zoom-in" onClick={heroClick} data-testid={`${kind}-hero-${item.id}`}>
          <img src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`} alt={item.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100"/>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 rounded-full bg-red-600/95 flex items-center justify-center shadow-lg"><div className="w-0 h-0 border-l-[14px] border-l-white border-y-[9px] border-y-transparent ml-1"/></div>
          </div>
        </div>
      ) : (
        <div className="aspect-[16/9] bg-gradient-to-br from-[color:var(--aii-green-tint)] to-slate-100 flex items-center justify-center text-[color:var(--aii-muted)]"><Sparkles size={28}/></div>
      )}
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div>
          <div className="font-display font-semibold text-base leading-tight">{item.name}</div>
          <div className="text-xs text-[color:var(--aii-muted)] flex flex-wrap gap-x-3 gap-y-1 mt-1">
            {item.date && <span className="flex items-center gap-1"><Clock size={10}/> {fmtDate(item.date)}{item.time ? ` · ${item.time}` : ""}</span>}
            {(item.venue || item.city) && <span className="flex items-center gap-1"><MapPin size={10}/> {locationLabel([item.venue, item.city])}</span>}
            {item.days > 1 && <span>{item.days} days</span>}
            {(item.price || 0) > 0 && <span className="font-semibold text-brand">{currencySymbol}{item.price}</span>}
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)]">
          {item.booked_count || 0} booked · {item.skipped_count || 0} skipped · <span className="text-brand">tap for details</span>
        </div>
        <div className="flex gap-2 mt-auto" data-noopen>
          <button onClick={(ev) => { ev.stopPropagation(); onDecide(item, "book"); }} disabled={busy} data-testid={`${kind}-book-${item.id}`}
            className={`flex-1 py-2 rounded-md font-semibold text-sm transition ${decided === "book" ? "bg-emerald-600 text-white" : "bg-brand text-white hover:bg-brand-dark"} disabled:opacity-60`}>
            {decided === "book" ? <><CheckCircle2 size={13} className="inline mr-1"/> Booked</> : "Book"}
          </button>
          <button onClick={(ev) => { ev.stopPropagation(); onDecide(item, "skip"); }} disabled={busy} data-testid={`${kind}-skip-${item.id}`}
            className={`flex-1 py-2 rounded-md font-semibold text-sm transition border ${decided === "skip" ? "bg-slate-200 text-[color:var(--aii-text-1)] border-slate-300" : "bg-white text-[color:var(--aii-text-2)] border-[color:var(--aii-border)] hover:bg-slate-50"} disabled:opacity-60`}>
            {decided === "skip" ? <><XCircle size={13} className="inline mr-1"/> Skipped</> : "Skip"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// ItemDetailModal — a single reusable "full details" modal for
// activities & trips. Renders media gallery, meta, itinerary
// (cities / hotels / taxis / meals for trips), notes, and lets
// the participant book/skip without leaving the modal.
// -------------------------------------------------------------
function ItemDetailModal({ item, kind, onClose, onDecide, onImageOpen, currencySymbol }) {
  if (!item) return null;
  const ytId = ytIdFrom(item.youtube_url);
  const decided = item.my_decision;
  const hasItinerary = kind === "trip" && (
    (item.cities || []).length + (item.hotels || []).length + (item.taxis || []).length + (item.meals || []).length > 0
  );
  const openImg = (i) => onImageOpen?.(item, i);
  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4 overflow-auto" onClick={onClose} data-testid="item-details-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl overflow-hidden shadow-2xl my-4" onClick={ev => ev.stopPropagation()}>
        {/* Media hero — YouTube gets an inline iframe here so participants can watch without leaving */}
        {ytId ? (
          <div className="aspect-[16/9] bg-black"><iframe title={item.name} src={`https://www.youtube.com/embed/${ytId}`} className="w-full h-full" allowFullScreen frameBorder="0"/></div>
        ) : item.video_url ? (
          <div className="aspect-[16/9] bg-black"><video src={item.video_url} controls autoPlay className="w-full h-full object-contain"/></div>
        ) : (item.images && item.images[0]) ? (
          <div className="aspect-[16/9] bg-slate-100 cursor-zoom-in" onClick={() => openImg(0)} data-testid="detail-hero-img">
            <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover"/>
          </div>
        ) : null}

        {/* Additional image thumbnails */}
        {item.images && item.images.length > 1 && (
          <div className="grid grid-cols-4 gap-1 p-1 bg-slate-50">
            {item.images.slice(1, 5).map((u, i) => (
              <div key={`${u}-${i}`} className="aspect-square overflow-hidden rounded cursor-zoom-in"
                   onClick={() => openImg(i + 1)} data-testid={`detail-thumb-${i + 1}`}>
                <img src={u} alt="" className="w-full h-full object-cover"/>
              </div>
            ))}
          </div>
        )}

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold">{kind === "trip" ? "Trip" : "Activity"} details</div>
              <h2 className="font-display font-bold text-xl leading-tight mt-0.5">{item.name}</h2>
            </div>
            <button onClick={onClose} className="text-[color:var(--aii-muted)] hover:text-[color:var(--aii-text-1)]" data-testid="close-details"><XCircle size={22}/></button>
          </div>

          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            {item.date && <Meta icon={<Clock size={12}/>} label="Date" value={`${fmtDate(item.date)}${item.time ? ` · ${item.time}` : ""}`}/>}
            {(item.venue || item.city) && <Meta icon={<MapPin size={12}/>} label="Location" value={locationLabel([item.venue, item.city, item.country])}/>}
            {(item.price || 0) > 0 && <Meta icon={<Sparkles size={12}/>} label="Price" value={<span className="font-bold text-brand">{currencySymbol}{item.price}</span>}/>}
            {(item.days || 0) > 1 && <Meta icon={<CalendarClock size={12}/>} label="Duration" value={`${item.days} days`}/>}
            {(item.booked_count > 0 || item.skipped_count > 0) && <Meta icon={<UserCheck size={12}/>} label="Bookings" value={`${item.booked_count || 0} booked · ${item.skipped_count || 0} skipped`}/>}
          </div>

          {/* Notes */}
          {item.notes && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold mb-1">Notes & description</div>
              <div className="text-sm whitespace-pre-wrap text-[color:var(--aii-text-1)] bg-[color:var(--aii-green-tint)]/40 p-3 rounded-md">{item.notes}</div>
            </div>
          )}

          {/* Trip itinerary — cities → hotels → taxis → meals */}
          {hasItinerary && (
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Itinerary & schedule</div>
              {(item.cities || []).length > 0 && <div className="text-sm"><span className="font-semibold">Cities:</span> {item.cities.join(" → ")}</div>}
              {(item.hotels || []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1">Hotels ({item.hotels.length})</div>
                  <ul className="text-sm space-y-1">{item.hotels.map((h, i) => (
                    <li key={`${item.id}-h-${i}-${h.hotel || h.city || ""}`} className="flex justify-between border-b border-[color:var(--aii-border)]/50 pb-1"><span>{h.city ? `${h.city} · ` : ""}<b>{h.hotel || "TBD"}</b></span><span className="text-[color:var(--aii-muted)]">{h.nights || 1} night(s)</span></li>
                  ))}</ul>
                </div>
              )}
              {(item.taxis || []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1">Transport ({item.taxis.length})</div>
                  <ul className="text-sm space-y-1">{item.taxis.map((t, i) => (
                    <li key={`${item.id}-t-${i}-${t.name || ""}`} className="flex justify-between"><span>🚕 {t.name || "Taxi"}</span></li>
                  ))}</ul>
                </div>
              )}
              {(item.meals || []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1">Meals ({item.meals.length})</div>
                  <ul className="text-sm space-y-1">{item.meals.map((m, i) => (
                    <li key={`${item.id}-m-${i}-${m.name || ""}`} className="flex justify-between"><span>🍽️ {m.name || "Meal"}</span></li>
                  ))}</ul>
                </div>
              )}
            </div>
          )}

          {/* Book / Skip buttons */}
          <div className="flex gap-2 pt-2 border-t border-[color:var(--aii-border)]">
            <button onClick={() => onDecide(item, "book")}
              className={`flex-1 py-2.5 rounded-md font-semibold text-sm ${decided === "book" ? "bg-emerald-600 text-white" : "bg-brand text-white hover:bg-brand-dark"}`}>
              {decided === "book" ? <><CheckCircle2 size={14} className="inline mr-1"/> Booked</> : "Book"}
            </button>
            <button onClick={() => onDecide(item, "skip")}
              className={`flex-1 py-2.5 rounded-md font-semibold text-sm border ${decided === "skip" ? "bg-slate-200 border-slate-300" : "bg-white border-[color:var(--aii-border)] hover:bg-slate-50"}`}>
              {decided === "skip" ? <><XCircle size={14} className="inline mr-1"/> Skipped</> : "Skip"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Meta = ({ icon, label, value }) => (
  <div className="flex flex-col">
    <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold flex items-center gap-1">{icon}{label}</div>
    <div className="text-sm">{value}</div>
  </div>
);

function UpcomingActivitiesPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/participant/activities"); setItems(data.activities || []); }
    catch (err) { console.debug("participant activities load:", err?.message); }
  };
  useEffect(() => { load(); }, []);
  const onDecide = async (item, decision) => {
    setBusy(item.id);
    try {
      await api.post(`/activities/${item.id}/decision`, { decision });
      toast.success(decision === "book" ? "Booked — see you there!" : "Noted, skipped");
      // Reflect immediately in modal if it's still open
      setSelected(prev => prev && prev.id === item.id ? { ...prev, my_decision: decision } : prev);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  if (items.length === 0) return null;
  const openImg = (item, idx) => setLightbox(openItemMedia(item, idx));
  return (
    <div className="aii-card p-5" data-testid="upcoming-activities-panel">
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1"><Sparkles size={11}/> Upcoming activities</div>
        <h3 className="font-display font-semibold text-lg">Tap a card for full details</h3>
        <p className="text-xs text-[color:var(--aii-muted)] mt-1">Approved activities from our planning team — tap any card to see notes, schedule and media before you book.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map(a => <BookSkipCard key={a.id} item={a} kind="activity" busy={busy === a.id} onDecide={onDecide} onOpen={setSelected} onImageOpen={openImg} currencySymbol="$"/>)}
      </div>
      {selected && <ItemDetailModal item={selected} kind="activity" onClose={() => setSelected(null)} onDecide={onDecide} onImageOpen={openImg} currencySymbol="$"/>}
      {lightbox && (
        <Lightbox items={lightbox.items} index={lightbox.index}
                  onClose={() => setLightbox(null)}
                  onNav={(dir) => setLightbox(l => l ? { ...l, index: (l.index + dir + l.items.length) % l.items.length } : l)}/>
      )}
    </div>
  );
}

function UpcomingTripsPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/participant/trips"); setItems(data.trips || []); }
    catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  const onDecide = async (item, decision) => {
    setBusy(item.id);
    try {
      await api.post(`/trips/${item.id}/decision`, { decision });
      toast.success(decision === "book" ? "Booked!" : "Noted, skipped");
      setSelected(prev => prev && prev.id === item.id ? { ...prev, my_decision: decision } : prev);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  if (items.length === 0) return null;
  const openImg = (imgs, idx) => setLightbox({ images: imgs, index: idx });
  return (
    <div className="aii-card p-5" data-testid="upcoming-trips-panel">
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1"><MapPin size={11}/> Upcoming trips</div>
        <h3 className="font-display font-semibold text-lg">Tap a trip for the full itinerary</h3>
        <p className="text-xs text-[color:var(--aii-muted)] mt-1">Approved trips from our organiser — tap to see hotels, meals, transport and schedule.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map(t => <BookSkipCard key={t.id} item={t} kind="trip" busy={busy === t.id} onDecide={onDecide} onOpen={setSelected} onImageOpen={openImg} currencySymbol="$"/>)}
      </div>
      {selected && <ItemDetailModal item={selected} kind="trip" onClose={() => setSelected(null)} onDecide={onDecide} onImageOpen={openImg} currencySymbol="$"/>}
      {lightbox && (
        <Lightbox items={lightbox.items} index={lightbox.index}
                  onClose={() => setLightbox(null)}
                  onNav={(dir) => setLightbox(l => l ? { ...l, index: (l.index + dir + l.items.length) % l.items.length } : l)}/>
      )}
    </div>
  );
}


const SERVICE_EMOJI = {
  currency: "💱", sim: "📱", laundry: "🧺", roomclean: "🧹", taxi: "🚕",
  bike: "🚲", scooter: "🛵", meal: "🍱", tea: "🍵", water: "💧",
  ironing: "👕", airport: "✈️", medical: "🩺", massage: "💆",
};
const SVC_EMOJI = (key) => SERVICE_EMOJI[key] || "🛎️";

const DESTINATION_COUNTRIES = [
  "India", "Nepal", "Sri Lanka", "South Africa", "Uganda", "Nigeria",
  "Kenya", "Bolivia", "Thailand", "Vietnam",
];
const PLACEMENT_AREAS = [
  "Medical", "Childcare", "Teaching", "Animal Care", "Elephant Care",
  "Construction", "Orphanage", "Women Empowerment", "Ayurveda",
  "Wildlife", "Sea Turtles Conservation", "Other",
];

// Compact event tile used on the dashboard. Clicking it switches to the Events tab.
function EventTile({ e, onOpen, todayTint = false }) {
  const imgs = (e.images && e.images.length > 0) ? e.images : (e.picture_url ? [e.picture_url] : []);
  const d = e.date ? new Date(e.date) : null;
  const day = d && !isNaN(d) ? d.getDate() : "";
  const mon = d && !isNaN(d) ? d.toLocaleString("en", { month: "short" }).toUpperCase() : "";
  return (
    <button onClick={onOpen} data-testid={`dash-event-${e.id}`}
      className="w-full text-left aii-card overflow-hidden group hover:shadow-lg transition-shadow flex sm:flex-col">
      <div className="relative w-24 sm:w-full flex-shrink-0 sm:aspect-[16/10] bg-slate-100 overflow-hidden">
        {imgs.length > 0 ? (
          <img src={imgs[0]} alt={e.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[color:var(--aii-muted)]"><ImgIcon size={24}/></div>
        )}
        <div className={`absolute inset-0 ${todayTint ? "bg-gradient-to-t from-amber-900/70 via-amber-900/10 to-transparent" : "bg-gradient-to-t from-black/60 via-transparent to-transparent"}`}/>
        {day && (
          <div className="absolute top-2 left-2 bg-white rounded shadow overflow-hidden text-center" style={{ minWidth: 40 }}>
            <div className={`text-[8px] font-bold tracking-widest px-1.5 py-0.5 ${todayTint ? "bg-amber-500 text-white" : "bg-brand text-white"}`}>{mon}</div>
            <div className="text-sm font-display font-bold px-1.5 py-0.5 leading-none">{day}</div>
          </div>
        )}
        {todayTint && (
          <div className="absolute top-2 right-2 bg-amber-500 text-white text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full shadow animate-pulse">TODAY</div>
        )}
      </div>
      <div className="p-3 flex-1 min-w-0">
        <div className="font-display font-semibold text-sm sm:text-base leading-tight line-clamp-2">{e.title}</div>
        {e.subtitle && <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5 line-clamp-1">{e.subtitle}</div>}
        <div className="flex items-center gap-3 text-[11px] mt-2 text-[color:var(--aii-text-2)] flex-wrap">
          {e.location && <span className="flex items-center gap-1"><MapPin size={10} className="text-brand"/> {e.location}</span>}
          {e.price ? <span className="font-semibold text-brand">{USD(e.price)}</span> : <span className="text-emerald-700 font-semibold text-[10px]">FREE</span>}
          {e.my_action === "booked" && <span className="text-emerald-700 font-semibold flex items-center gap-0.5"><CheckCircle2 size={10}/> Booked</span>}
        </div>
      </div>
    </button>
  );
}

function DashboardPanel({ me, onGoto }) {
  const [catalog, setCatalog] = useState([]);
  const [reqBusy, setReqBusy] = useState("");
  const [events, setEvents] = useState([]);
  const [needsCform, setNeedsCform] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [cropFile, setCropFile] = useState(null); // File object queued for the cropper
  const isIndian = me?.is_indian_placement;

  useEffect(() => { api.get("/service-catalog").then(({data})=>setCatalog(data)).catch(()=>{}); }, []);
  useEffect(() => { api.get("/events").then(({data}) => setEvents(data || [])).catch(()=>{}); }, []);
  useEffect(() => {
    Promise.all([
      api.get("/participant/cform/mine").catch(() => ({ data: [] })),
      api.get("/threads?kind=disclaimer").catch(() => ({ data: [] })),
    ]).then(([c, d]) => {
      setNeedsCform((c.data || []).length === 0);
      setNeedsConsent((d.data || []).length === 0);
    });
  }, []);

  const quickRequest = async (key) => {
    const svc = catalog.find((c) => c.key === key);
    if (!svc) { toast.error("Service not available yet"); return; }
    setReqBusy(key);
    try {
      await api.post("/service-requests", { service_id: svc.id, quantity: 1 });
      toast.success(`${svc.label} — request sent to GEHM`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setReqBusy(""); }
  };

  // Bucket events into "today" / "next 14 days". Wrapped in useMemo
  // so we don't re-filter the full events list on every unrelated
  // DashboardPanel re-render (WebSocket ticks, tab focus, etc.).
  const today = new Date().toISOString().slice(0, 10);
  const in14 = new Date(); in14.setDate(in14.getDate() + 14);
  const in14Iso = in14.toISOString().slice(0, 10);
  const todaysEvents = useMemo(() => events.filter((e) => e.date === today), [events, today]);
  const upcomingEvents = useMemo(() => (
    events.filter((e) => e.date && e.date > today && e.date <= in14Iso).slice(0, 4)
  ), [events, today, in14Iso]);

  // Skeleton state while /api/participant/me is loading.
  if (!me) {
    return (
      <div className="space-y-4" data-testid="dashboard-skeleton">
        <div className="aii-card p-5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-slate-200 animate-pulse"/>
            <div className="flex-1 space-y-2">
              <div className="h-3 w-24 bg-slate-200 rounded animate-pulse"/>
              <div className="h-6 w-48 bg-slate-200 rounded animate-pulse"/>
              <div className="h-3 w-64 bg-slate-200 rounded animate-pulse"/>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[0,1].map(i => (
            <div key={i} className="aii-card p-5 space-y-3">
              <div className="h-3 w-24 bg-slate-200 rounded animate-pulse"/>
              <div className="h-5 w-40 bg-slate-200 rounded animate-pulse"/>
              <div className="h-3 w-full bg-slate-100 rounded animate-pulse"/>
              <div className="h-3 w-full bg-slate-100 rounded animate-pulse"/>
              <div className="h-3 w-3/4 bg-slate-100 rounded animate-pulse"/>
            </div>
          ))}
        </div>
        <div className="aii-card p-5">
          <div className="h-4 w-40 bg-slate-200 rounded animate-pulse mb-3"/>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
            {[0,1,2,3,4].map(i => (
              <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse"/>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const b = me.booking;
  const a = me.allocation;
  // ParticipantFeedbackCard self-fetches — it renders null when the
  // partner hasn't requested feedback yet, so it's safe to always mount.

  return (
    <div className="space-y-4">
      {/* Partner feedback prompt — only renders when partner has requested */}
      <ParticipantFeedbackCard />
      {/* Mandatory action banners */}
      {((needsCform && isIndian !== false) || needsConsent) && (
        <div className="space-y-2" data-testid="mandatory-actions">
          {needsCform && isIndian !== false && (
            <button onClick={() => onGoto("cform")} data-testid="banner-cform"
              className="w-full text-left rounded-lg border border-red-300 bg-red-50 hover:bg-red-100 transition text-red-900 px-4 py-3 text-sm flex items-start gap-2 shadow-sm">
              <ShieldAlert size={18} className="flex-shrink-0 mt-0.5"/>
              <div className="flex-1">
                <div className="font-semibold">Action required: Submit your C-Form</div>
                <div className="text-xs mt-0.5">Mandatory under India Foreigners Act — takes 3 minutes.</div>
              </div>
              <ArrowRight size={16} className="flex-shrink-0 mt-1"/>
            </button>
          )}
          {needsConsent && (
            <button onClick={() => onGoto("disclaimer")} data-testid="banner-consent"
              className="w-full text-left rounded-lg border border-red-300 bg-red-50 hover:bg-red-100 transition text-red-900 px-4 py-3 text-sm flex items-start gap-2 shadow-sm">
              <ShieldAlert size={18} className="flex-shrink-0 mt-0.5"/>
              <div className="flex-1">
                <div className="font-semibold">Action required: Sign the Consent form</div>
                <div className="text-xs mt-0.5">Photo &amp; video consent — mandatory before your programme starts.</div>
              </div>
              <ArrowRight size={16} className="flex-shrink-0 mt-1"/>
            </button>
          )}
        </div>
      )}
      {/* Welcome + avatar */}
      <div className="aii-card p-5">
        <div className="flex items-center gap-4">
          <div className="relative">
            {me?.profile_pic_url ? (
              <ImgWithFallback src={me.profile_pic_url} alt="Me"
                               className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-4 border-[color:var(--aii-green-tint)]"
                               fallbackLabel={(me?.user?.name || "?").slice(0, 12)}
                               data-testid="participant-avatar"/>
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-bold flex items-center justify-center font-display text-2xl">{(me?.user?.name || "?")[0]}</div>
            )}
            {/* Participant photo upload — click the pencil to swap the pic
                from the dashboard without needing a C-Form (non-India
                participants don't file a C-Form, so this is the only path). */}
            <label htmlFor="participant-pic-upload"
                   data-testid="participant-pic-upload-btn"
                   title={me?.profile_pic_url ? "Change photo" : "Add your photo"}
                   className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brand text-white flex items-center justify-center border-2 border-white cursor-pointer hover:bg-brand-dark shadow-md">
              <Camera size={13}/>
            </label>
            <input id="participant-pic-upload" type="file" accept="image/*" className="hidden"
                   data-testid="participant-pic-upload-input"
                   onChange={(e) => {
                     const file = e.target.files?.[0];
                     if (!file) return;
                     if (file.size > 8 * 1024 * 1024) { toast.error("Photo too large (max 8 MB)"); e.target.value = ""; return; }
                     // The cropper now handles HEIC natively via a lazy
                     // heic2any conversion, so every image type flows
                     // through the same "crop → compress → upload" path.
                     setCropFile(file);
                     e.target.value = "";
                   }}/>
          </div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Welcome</div>
            <h1 className="text-xl sm:text-2xl font-display font-semibold truncate">{me?.user?.name}</h1>
            <div className="text-xs text-[color:var(--aii-muted)] truncate">{me?.user?.email}</div>
            {me?.profile_pic_url && (
              <button type="button" onClick={async () => {
                if (!window.confirm("Remove your dashboard photo?")) return;
                try {
                  await api.delete("/participant/me/profile-pic");
                  toast.success("Photo removed");
                  window.dispatchEvent(new Event("participant:refresh"));
                } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
              }} data-testid="participant-pic-remove"
                 className="text-[11px] text-red-600 hover:underline mt-1">Remove photo</button>
            )}
          </div>
        </div>
        {!me?.profile_pic_url && (
          <div className="mt-4 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-2.5">
            {isIndian !== false ? (
              <>Tap the camera icon to add your photo, or complete your <button onClick={()=>onGoto("cform")} className="underline font-semibold" data-testid="cf-cta">C-Form</button> to add passport details too.</>
            ) : (
              <>Tap the camera icon on your avatar to add your dashboard photo.</>
            )}
          </div>
        )}
      </div>

      {/* Programme + Assignment */}
      {b ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="aii-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">My Programme</div>
            <div className="font-display text-lg font-semibold" data-testid="my-programme">{b.programme || "—"}</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[color:var(--aii-muted)]">Duration</span><span className="font-medium">{fmtDate(b.start_date) || "—"} → {fmtDate(b.end_date) || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[color:var(--aii-muted)]">Destination</span><span className="font-medium" data-testid="my-destination">{b.destination || <em className="text-[color:var(--aii-muted)] font-normal">—</em>}</span></div>
              <div className="flex justify-between"><span className="text-[color:var(--aii-muted)]">City</span><span className="font-medium" data-testid="my-city">{b.city || <em className="text-[color:var(--aii-muted)] font-normal">—</em>}</span></div>
              <div className="flex justify-between"><span className="text-[color:var(--aii-muted)]">Nationality</span><span className="font-medium" data-testid="my-nationality">{b.nationality || me.latest_cform?.nationality || <em className="text-[color:var(--aii-muted)] font-normal">—</em>}</span></div>
            </div>
          </div>
          <div className="aii-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">My Assignments</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between border-b border-[color:var(--aii-border)] pb-2">
                <span className="text-[color:var(--aii-muted)]">Project</span>
                <span className="font-medium" data-testid="my-project">{b.project_placement || <em className="text-[color:var(--aii-muted)] font-normal">not yet assigned</em>}</span>
              </div>
              <div className="flex items-center justify-between border-b border-[color:var(--aii-border)] pb-2">
                <span className="text-[color:var(--aii-muted)]">Accommodation</span>
                <span className="font-medium" data-testid="my-accommodation-name">{a?.accommodation_name || <em className="text-[color:var(--aii-muted)] font-normal">not yet assigned</em>}</span>
              </div>
              {/* Placement (PCWM assigns this via /program-bookings PATCH; the
                  authoritative field is `placement_name`. Falls back to the
                  legacy `placement` free-text for pre-migration bookings.) */}
              <div className="flex items-center justify-between" data-testid="my-placement-row">
                <span className="text-[color:var(--aii-muted)]">Placement</span>
                <span className="font-medium" data-testid="my-placement">{b.placement_name || b.placement || <em className="text-[color:var(--aii-muted)] font-normal">not yet assigned by PCWM</em>}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="aii-card p-5 text-sm text-[color:var(--aii-muted)]">No booking linked yet — please contact your Aii Group coordinator.</div>
      )}

      {/* Accommodation card — shows the full property address (with Maps link),
          plus Export PDF + Share-on-WhatsApp buttons so participants can text
          their arrival card to hosts / family before they land. */}
      {a?.accommodation_name && (
        <div className="aii-card p-5" data-testid="my-accommodation">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-[color:var(--aii-green-tint)] text-brand flex items-center justify-center flex-shrink-0">
              <MapPin size={20}/>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-brand font-bold">You&rsquo;re staying at</div>
                  <div className="font-display font-semibold text-lg leading-tight">{a.accommodation_name}</div>
                  <div className="text-[11px] text-[color:var(--aii-muted)] uppercase tracking-wider">{a.accommodation_type}{a.accommodation_city && ` · ${a.accommodation_city}, ${a.accommodation_country}`}</div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end print:hidden">
                  <button onClick={() => window.print()} data-testid="print-details-btn"
                    className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1"
                    title="Save arrival card as PDF">
                    <Download size={11}/> Export PDF
                  </button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(
                      `Hey! Here's where I'll be staying with Aii Group:\n\n` +
                      `📍 ${a.accommodation_name}${a.accommodation_type ? ` (${a.accommodation_type})` : ""}\n` +
                      `${a.accommodation_city ? `${a.accommodation_city}, ${a.accommodation_country}\n` : ""}` +
                      `${a.accommodation_address ? `\n${a.accommodation_address}\n` : ""}` +
                      `\nMap: https://maps.google.com/?q=${encodeURIComponent(a.accommodation_address || `${a.accommodation_name} ${a.accommodation_city || ""}`)}\n` +
                      `\nProgramme: ${b?.programme || "—"}\n` +
                      `Dates: ${b?.start_date || "?"} → ${b?.end_date || "?"}\n` +
                      `Room ${a.room?.split("·").pop()?.trim() || a.room || "—"} · Bed ${a.bed_no || "—"}`
                    )}`}
                    target="_blank" rel="noopener noreferrer" data-testid="share-wa-btn"
                    className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1"
                    title="Share arrival card via WhatsApp">
                    <Send size={11}/> Share on WhatsApp
                  </a>
                </div>
              </div>
              {a.accommodation_address && (
                <div className="mt-2 text-sm text-[color:var(--aii-text-2)] leading-snug flex items-start gap-1.5">
                  <MapPin size={12} className="text-brand flex-shrink-0 mt-0.5"/>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(a.accommodation_address)}`} target="_blank" rel="noopener noreferrer"
                    className="whitespace-pre-wrap hover:text-brand underline decoration-dotted underline-offset-2" data-testid="my-accommodation-address">
                    {a.accommodation_address}
                  </a>
                </div>
              )}
              <div className="mt-2 flex gap-3 text-xs text-[color:var(--aii-text-2)]">
                <span><b className="text-brand">Room</b> {a.room?.split("·").pop()?.trim() || a.room || "—"}</span>
                <span><b className="text-brand">Bed</b> {a.bed_no || "—"}</span>
              </div>
              {Array.isArray(a.accommodation_photos) && a.accommodation_photos.length > 0 && (
                <div className="mt-3" data-testid="my-accommodation-photos">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold mb-1.5">Preview</div>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {a.accommodation_photos.map(fid => (
                      <img key={fid} src={`${API}/files/${fid}?auth=${getToken()}`} alt=""
                           className="h-24 w-32 object-cover rounded border border-[color:var(--aii-border)] flex-shrink-0"
                           data-testid={`my-accommodation-photo-${fid}`}/>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick shortcuts to GEHM */}
      <div className="aii-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Quick help</div>
            <h3 className="font-display font-semibold text-lg">One-tap requests to your GEHM</h3>
          </div>
        </div>
        {catalog.length === 0 ? (
          <div className="text-xs text-[color:var(--aii-muted)] py-4 text-center">Loading services…</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
            {catalog.map((s) => (
              <button key={s.id} onClick={()=>quickRequest(s.key)} disabled={reqBusy === s.key}
                data-testid={`quick-${s.key}`}
                className="flex items-center gap-3 p-3 rounded-lg border border-[color:var(--aii-border)] hover:border-brand hover:bg-[color:var(--aii-green-tint)] transition disabled:opacity-50 text-left">
                <span className="text-2xl flex-shrink-0">{SVC_EMOJI(s.key)}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold leading-tight truncate">{s.label}</div>
                  <div className="text-[11px] text-[color:var(--aii-muted)]">
                    {s.price ? <>{USD(s.price)}<span className="text-[9px] uppercase tracking-wider"> /req</span></> : <span className="text-emerald-700 font-semibold">FREE</span>}
                  </div>
                  {reqBusy === s.key && <div className="text-[10px] text-brand">Sending…</div>}
                </div>
              </button>
            ))}
          </div>
        )}
        <div className="text-[11px] text-[color:var(--aii-muted)] mt-2">Sent instantly to your Guest Experience &amp; Housekeeping Manager. Track status in the Services tab.</div>
      </div>

      {/* Placement briefing — AI-generated from PCWM's placement + GEHM's accommodation */}
      <ProjectOverviewPanel/>

      {/* Curated tours in your destination */}
      <CuratedToursPanel/>
      {/* Approved activities open for booking */}
      <UpcomingActivitiesPanel/>
      {/* Approved trips open for booking */}
      <UpcomingTripsPanel/>

      {/* My Memories — Pictures + Video Testimonial links logged by SMCM on
          the Pictures & Videos page. Only renders when at least one is
          populated so participants pre-arrival aren't confused by empty
          state. */}
      {(b?.pictures_link || b?.testimonials_link) && (
        <div className="aii-card p-5" data-testid="my-memories">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-[color:var(--aii-green-tint)] text-brand flex items-center justify-center flex-shrink-0">
              <Camera size={20}/>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Your programme memories</div>
              <div className="font-display font-semibold text-lg leading-tight">Pictures &amp; video testimonial</div>
              <p className="text-xs text-[color:var(--aii-text-2)] mt-0.5">Curated by our Social Media &amp; Content Manager — feel free to view, download and share.</p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {b.pictures_link && (
                  <a href={b.pictures_link} target="_blank" rel="noopener noreferrer"
                     data-testid="my-memories-pics"
                     className="flex items-center gap-2 px-3 py-2.5 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] font-semibold text-sm">
                    <ImgIcon size={14}/> View / download pictures
                    <ArrowRight size={12} className="ml-auto"/>
                  </a>
                )}
                {b.testimonials_link && (
                  <a href={b.testimonials_link} target="_blank" rel="noopener noreferrer"
                     data-testid="my-memories-testi"
                     className="flex items-center gap-2 px-3 py-2.5 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] font-semibold text-sm">
                    <Youtube size={14}/> Watch video testimonial
                    <ArrowRight size={12} className="ml-auto"/>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Today's events — highlighted band */}
      {todaysEvents.length > 0 && (
        <div className="aii-card p-5 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200" data-testid="todays-events">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold flex items-center gap-1"><Sparkles size={11}/> Happening today</div>
              <h3 className="font-display font-semibold text-lg text-amber-900">Don&rsquo;t miss out!</h3>
            </div>
            <button onClick={() => onGoto("activities")} data-testid="dash-all-today" className="text-xs text-amber-800 hover:underline flex items-center gap-1">All events <ArrowRight size={12}/></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {todaysEvents.map((e) => <EventTile key={e.id} e={e} onOpen={()=>onGoto("activities")} todayTint/>)}
          </div>
        </div>
      )}

      {/* Upcoming events (next 14 days) */}
      {(upcomingEvents.length > 0 || todaysEvents.length === 0) && (
        <div className="aii-card p-5" data-testid="upcoming-events-preview">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Next 14 days</div>
              <h3 className="font-display font-semibold text-lg">Upcoming events</h3>
            </div>
            <button onClick={() => onGoto("activities")} data-testid="dash-all-upcoming" className="text-xs text-brand hover:underline flex items-center gap-1">See all <ArrowRight size={12}/></button>
          </div>
          {upcomingEvents.length === 0 ? (
            <div className="text-center py-6 text-sm text-[color:var(--aii-muted)]">
              <CalendarClock size={28} className="mx-auto opacity-40 mb-2"/>
              No events lined up yet — new experiences drop here as they&rsquo;re announced.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {upcomingEvents.map((e) => <EventTile key={e.id} e={e} onOpen={()=>onGoto("activities")}/>)}
            </div>
          )}
        </div>
      )}
      {/* Danger zone — participant self-service account deletion */}
      <DangerZone />

      {/* Profile-pic cropper — mounted only when a file is queued */}
      {cropFile && (
        <PhotoCropperModal
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onSave={async (blob) => {
            const fd = new FormData();
            fd.append("file", blob, "profile.jpg");
            try {
              await api.post("/participant/me/profile-pic", fd, { headers: { "Content-Type": "multipart/form-data" } });
              toast.success("Photo updated");
              window.dispatchEvent(new Event("participant:refresh"));
              setCropFile(null);
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Upload failed");
            }
          }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// PhotoCropperModal — square-crop step for participant profile pics
// so a rectangular selfie doesn't get squished by CSS `object-cover`.
// Renders the source image inside a fixed 320×320 canvas; the user
// pans (drag) and zooms (slider) to compose. Exports a 512×512 JPEG.
// ------------------------------------------------------------------
function PhotoCropperModal({ file, onCancel, onSave }) {
  const canvasRef = useRef(null);
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prepMsg, setPrepMsg] = useState(null); // "Converting HEIC…" while heic2any runs
  const VIEW = 320;
  const OUTPUT = 512;

  useEffect(() => {
    if (!file) return;
    let objectUrl = null;
    let cancelled = false;

    const loadImage = (source) => new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("decode failed"));
      image.src = source;
    });

    const uploadRaw = async () => {
      // Last-ditch fallback: browser can't decode this at all — send
      // raw bytes and let the backend cope. Keeps the participant
      // moving instead of trapping them on a blank cropper.
      const fd = new FormData();
      fd.append("file", file, file.name || "profile.jpg");
      const t = toast.loading("Uploading photo…");
      try {
        await api.post("/participant/me/profile-pic", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Photo updated", { id: t });
        window.dispatchEvent(new Event("participant:refresh"));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed — try a JPG or PNG", { id: t });
      }
      onCancel();
    };

    (async () => {
      const fname = (file.name || "").toLowerCase();
      const ctype = (file.type || "").toLowerCase();
      const isHeic = ctype === "image/heic" || ctype === "image/heif"
                     || fname.endsWith(".heic") || fname.endsWith(".heif");
      try {
        let source = file;
        if (isHeic) {
          // Lazy-load heic2any so a participant who never uploads a HEIC
          // photo pays zero bytes for the library. First call downloads
          // ~1 MB gzipped worker; subsequent calls are cached by the
          // browser's HTTP cache.
          setPrepMsg("Converting HEIC photo…");
          const { default: heic2any } = await import(/* webpackChunkName: "heic2any" */ "heic2any");
          const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
          source = Array.isArray(converted) ? converted[0] : converted;
        }
        if (cancelled) return;
        objectUrl = URL.createObjectURL(source);
        const image = await loadImage(objectUrl);
        if (cancelled) return;
        const base = Math.max(VIEW / image.width, VIEW / image.height);
        image._baseScale = base;
        setImg(image);
        setZoom(1);
        setOffset({ x: 0, y: 0 });
        setPrepMsg(null);
      } catch (_e) {
        if (!cancelled) await uploadRaw();
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, onCancel]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, VIEW, VIEW);
    ctx.fillStyle = "#f1f5f9";
    ctx.fillRect(0, 0, VIEW, VIEW);
    const scale = (img._baseScale || 1) * zoom;
    const dw = img.width * scale;
    const dh = img.height * scale;
    const dx = (VIEW - dw) / 2 + offset.x;
    const dy = (VIEW - dh) / 2 + offset.y;
    ctx.drawImage(img, dx, dy, dw, dh);
    // Circular mask overlay (visual only)
    ctx.save();
    ctx.globalCompositeOperation = "destination-in";
    ctx.beginPath();
    ctx.arc(VIEW / 2, VIEW / 2, VIEW / 2 - 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }, [img, zoom, offset]);

  const onPointerDown = (e) => setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  const onPointerMove = (e) => {
    if (!dragStart) return;
    setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const onPointerUp = () => setDragStart(null);

  const save = async () => {
    if (!img) return;
    setBusy(true);
    // Render at OUTPUT resolution for a crisp avatar
    const out = document.createElement("canvas");
    out.width = OUTPUT; out.height = OUTPUT;
    const ctx = out.getContext("2d");
    const scale = (img._baseScale || 1) * zoom * (OUTPUT / VIEW);
    const dw = img.width * scale;
    const dh = img.height * scale;
    const dx = (OUTPUT - dw) / 2 + offset.x * (OUTPUT / VIEW);
    const dy = (OUTPUT - dh) / 2 + offset.y * (OUTPUT / VIEW);
    ctx.fillStyle = "#f1f5f9";
    ctx.fillRect(0, 0, OUTPUT, OUTPUT);
    ctx.drawImage(img, dx, dy, dw, dh);

    // Iteratively lower JPEG quality until we're under ~500 KB (a good
    // sweet spot for a 512² avatar — visibly indistinguishable from the
    // 0.9 baseline but ~4× smaller for participants on slow networks).
    // Stops at q=0.55 so we never ship a mushy artefact.
    const TARGET = 500 * 1024;
    const toBlob = (q) => new Promise((resolve) => out.toBlob((b) => resolve(b), "image/jpeg", q));
    let quality = 0.9;
    let blob = await toBlob(quality);
    while (blob && blob.size > TARGET && quality > 0.55) {
      quality = Math.max(0.55, quality - 0.1);
      const next = await toBlob(quality);
      if (!next) break;
      blob = next;
    }
    if (!blob) { toast.error("Failed to render photo"); setBusy(false); return; }
    onSave(blob).finally(() => setBusy(false));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="photo-cropper-modal" onClick={() => !busy && onCancel()}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[color:var(--aii-border)]">
          <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Crop your photo</div>
          <h3 className="font-display text-lg font-semibold">Frame it perfectly</h3>
          <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Drag to reposition · use the slider to zoom.</p>
        </div>
        <div className="p-4 flex flex-col items-center gap-3">
          <div className="relative rounded-full overflow-hidden border-4 border-[color:var(--aii-green-tint)] shadow-inner"
               style={{ width: VIEW, height: VIEW, touchAction: "none", cursor: dragStart ? "grabbing" : "grab" }}
               onMouseDown={onPointerDown} onMouseMove={onPointerMove} onMouseUp={onPointerUp} onMouseLeave={onPointerUp}
               onTouchStart={(e) => { const t = e.touches[0]; onPointerDown({ clientX: t.clientX, clientY: t.clientY }); }}
               onTouchMove={(e) => { const t = e.touches[0]; onPointerMove({ clientX: t.clientX, clientY: t.clientY }); }}
               onTouchEnd={onPointerUp}
               data-testid="photo-cropper-canvas-wrap">
            <canvas ref={canvasRef} width={VIEW} height={VIEW} className="block"/>
            {prepMsg && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/85 backdrop-blur-sm text-center px-4" data-testid="photo-cropper-prep">
                <div>
                  <div className="w-8 h-8 mx-auto mb-2 border-2 border-brand border-t-transparent rounded-full animate-spin"/>
                  <div className="text-xs font-semibold text-brand">{prepMsg}</div>
                  <div className="text-[10px] text-[color:var(--aii-muted)] mt-0.5">Takes a few seconds on the first HEIC upload…</div>
                </div>
              </div>
            )}
          </div>
          <label className="w-full text-xs text-[color:var(--aii-muted)] uppercase tracking-widest font-semibold flex items-center gap-2">
            Zoom
            <input type="range" min="1" max="4" step="0.05" value={zoom}
                   onChange={e => setZoom(parseFloat(e.target.value))}
                   data-testid="photo-cropper-zoom"
                   className="flex-1"/>
          </label>
        </div>
        <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={busy} data-testid="photo-cropper-cancel"
                  className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={save} disabled={busy || !img} data-testid="photo-cropper-save"
                  className="px-4 py-2 text-sm bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5">
            <CheckCircle2 size={13}/> {busy ? "Uploading…" : "Use photo"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Participant Portal · Danger zone card at the bottom of Home.
 *
 * Deletes ONLY the participant's login row + push subscriptions — the
 * program_booking, sales, arrivals, C-Form, feedback and attendance rows
 * stay put for Aii Group's compliance/audit records. Two-step confirm
 * (button → modal with explicit-text confirmation) so it isn't tapped
 * accidentally on a phone.
 */
function DangerZone() {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const del = async () => {
    setBusy(true);
    try {
      await api.delete("/participant/me");
      toast.success("Your account has been deleted. Goodbye!");
      setTimeout(() => logout?.(), 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); setBusy(false); }
  };

  return (
    <>
      <div className="aii-card p-5 border-l-4 border-red-500" data-testid="danger-zone">
        <div className="flex items-start gap-3 flex-wrap">
          <AlertOctagon size={22} className="text-red-600 flex-shrink-0 mt-0.5"/>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-red-700 font-bold">Danger zone</div>
            <div className="font-display font-semibold mt-0.5">Delete my profile</div>
            <p className="text-xs text-[color:var(--aii-muted)] mt-1 leading-relaxed max-w-lg">
              Closes your Aii Group participant login. Your booking, C-Form, and payment records are retained by Aii Group + your sending partner for compliance. You&rsquo;ll be signed out immediately.
            </p>
          </div>
          <button
            onClick={() => { setOpen(true); setConfirmText(""); }}
            data-testid="delete-profile-btn"
            className="text-xs px-3 py-2 border border-red-500 text-red-700 rounded hover:bg-red-50 font-semibold flex items-center gap-1.5 whitespace-nowrap">
            <Trash2 size={12}/> Delete my profile
          </button>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()} data-testid="delete-profile-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between">
              <div>
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><AlertOctagon size={16} className="text-red-600"/> Delete your profile?</h3>
                <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">This closes your login. Your booking records stay with Aii Group.</p>
              </div>
              <button onClick={() => setOpen(false)} disabled={busy} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1 disabled:opacity-50"><XCircle size={16}/></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm">Type <b>DELETE</b> below to confirm — this cannot be undone.</p>
              <input
                data-testid="delete-profile-confirm-input"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoFocus
                autoComplete="off"
                className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm font-mono"
              />
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button onClick={() => setOpen(false)} disabled={busy}
                      className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                data-testid="delete-profile-confirm"
                onClick={del}
                disabled={busy || confirmText.trim() !== "DELETE"}
                className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 font-semibold disabled:opacity-50 flex items-center gap-1.5">
                <Trash2 size={12}/> {busy ? "Deleting…" : "Delete my profile"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
// Simpler cousin of the C-Form panel: same passport / visa / travel fields but
// no India-specific compliance messaging. Files (passport pic, visa pic, etc.)
// live in the Documents tab so this stays a pure text form.
function GeneralInfoPanel() {
  const [f, setF] = useState({
    passport_no: "", nationality: "", date_of_birth: "",
    visa_no: "", visa_type: "", visa_issued_at: "", visa_valid_till: "",
    address_home: "", address_destination: "", arrival_date: "", departure_date: "",
    purpose: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/participant/general-info").then(({ data }) => {
      if (data && Object.keys(data).length) setF(prev => ({ ...prev, ...data }));
    }).finally(() => setLoaded(true));
  }, []);
  const save = async () => {
    setBusy(true);
    try { await api.post("/participant/general-info", f); toast.success("General info saved — visible to your BDOM"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  if (!loaded) return <div className="text-sm text-[color:var(--aii-muted)] text-center py-6" data-testid="gi-loading">Loading…</div>;
  const fields = [
    ["passport_no", "Passport No"], ["nationality", "Nationality"],
    ["date_of_birth", "DOB (YYYY-MM-DD)"], ["visa_no", "Visa No"],
    ["visa_type", "Visa Type"], ["visa_issued_at", "Visa Issued At"],
    ["visa_valid_till", "Visa Valid Till"],
    ["arrival_date", "Arrival Date"], ["departure_date", "Departure Date"],
  ];
  return (
    <div className="space-y-4" data-testid="general-info-panel">
      <div className="rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-4 py-3 text-sm flex items-start gap-2">
        <ShieldCheck size={18} className="flex-shrink-0 mt-0.5"/>
        <div>
          <div className="font-semibold">General Information</div>
          <div className="text-xs mt-0.5">Share your passport, visa and travel dates so your BDOM can support you if anything is lost or misplaced. File uploads (passport scan, visa copy, etc.) live under the <b>Documents</b> tab.</div>
        </div>
      </div>
      <div className="aii-card p-5 space-y-3">
        <h2 className="font-display font-semibold text-lg">Your details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {fields.map(([k, l]) => (
            <label key={k} className="block">
              <div className="text-xs text-[color:var(--aii-muted)]">{l}</div>
              <input className="input-aii w-full" value={f[k] || ""} onChange={(e)=>setF({ ...f, [k]: e.target.value })} data-testid={`gi-${k}`}/>
            </label>
          ))}
          <label className="block sm:col-span-2">
            <div className="text-xs text-[color:var(--aii-muted)]">Home address</div>
            <input className="input-aii w-full" value={f.address_home || ""} onChange={(e)=>setF({ ...f, address_home: e.target.value })} data-testid="gi-address_home"/>
          </label>
          <label className="block sm:col-span-2">
            <div className="text-xs text-[color:var(--aii-muted)]">Destination address (if known)</div>
            <input className="input-aii w-full" value={f.address_destination || ""} onChange={(e)=>setF({ ...f, address_destination: e.target.value })} data-testid="gi-address_destination"/>
          </label>
          <label className="block sm:col-span-2">
            <div className="text-xs text-[color:var(--aii-muted)]">Purpose of visit</div>
            <input className="input-aii w-full" value={f.purpose || ""} onChange={(e)=>setF({ ...f, purpose: e.target.value })} data-testid="gi-purpose"/>
          </label>
          <label className="block sm:col-span-2">
            <div className="text-xs text-[color:var(--aii-muted)]">Notes (allergies, medical, emergency contact…)</div>
            <textarea rows={3} className="input-aii w-full" value={f.notes || ""} onChange={(e)=>setF({ ...f, notes: e.target.value })} data-testid="gi-notes"/>
          </label>
        </div>
        <button onClick={save} disabled={busy} data-testid="gi-save"
          className="w-full py-3 bg-brand text-white rounded font-semibold hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2">
          <Send size={14}/> {busy ? "Saving…" : "Save details"}
        </button>
      </div>
    </div>
  );
}


// Helpers for CFormPanel — hoisted out so React doesn't remount the input
// on every keystroke (which would eat focus mid-typing).
function CFSection({ letter, title, children }) {
  return (
    <div className="border border-[color:var(--aii-border)] rounded-lg p-4 space-y-3 bg-white">
      <div className="flex items-baseline gap-2">
        <span className="text-brand font-display font-bold text-lg">{letter}.</span>
        <h3 className="font-semibold">{title}</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">{children}</div>
    </div>
  );
}
function CFField({ f, setF, k, label, type = "text", required = false, span = 1, options = null }) {
  return (
    <label className={`block ${span === 2 ? "sm:col-span-2" : ""}`}>
      <div className="text-xs text-[color:var(--aii-muted)]">{label}{required && <span className="text-red-600 ml-0.5">*</span>}</div>
      {options ? (
        <select value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="input-aii w-full" data-testid={`cf-${k}`}>
          <option value="">— pick —</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={type} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="input-aii w-full" data-testid={`cf-${k}`}/>
      )}
    </label>
  );
}
function CFUploadBox({ file, setter, label, tid, required = false }) {
  return (
    <label className="block cursor-pointer">
      <div className="text-xs text-[color:var(--aii-muted)] mb-1">{label}{required && <span className="text-red-600 ml-0.5">*</span>}</div>
      <div className={`border-2 border-dashed rounded p-3 text-center hover:bg-[color:var(--aii-green-tint)] text-xs ${required && !file ? "border-red-300" : "border-[color:var(--aii-border)]"}`}>
        <Upload size={16} className="mx-auto text-brand"/>
        <div className="mt-1 truncate">{file ? file.name : "Click to upload"}</div>
      </div>
      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setter(e.target.files?.[0])} data-testid={tid}/>
    </label>
  );
}

function CFormPanel() {
  // 11-section FRRO Form C wizard-style layout. Booking data auto-fills the
  // fields the ops team already knows about (name / nationality / arrival
  // flight / arrival date) so participants only fill the ~15 truly personal
  // fields. Passport photo page + visa copy are mandatory uploads.
  const [f, setF] = useState({
    // A. Personal
    surname: "", given_names: "", gender: "", date_of_birth: "", nationality: "", special_category: "",
    // B. Permanent address (home country)
    home_address: "", home_city: "", home_state: "", home_postal_code: "", home_country: "",
    // C. Contact
    mobile_home_country: "", whatsapp_number: "", email: "",
    emergency_contact_name: "", emergency_contact_relationship: "", emergency_contact_number: "",
    // D. Passport
    passport_no: "", passport_issue_date: "", passport_expiry_date: "", passport_place_of_issue: "",
    // E. Visa
    visa_number: "", visa_type: "", visa_subtype: "", visa_issue_date: "", visa_expiry_date: "", visa_place_of_issue: "",
    // F. Travel
    flight_number: "", arrival_airport: "", arrival_city: "", arrival_date: "", arrival_time: "",
    country_of_departure: "", city_of_departure: "",
    // G. Arrival at accommodation
    checkin_date: "", checkin_time: "", expected_checkout_date: "", total_nights: "", intended_duration_days: "",
    // H. Visit info
    purpose_of_visit: "", employed_in_india: "No",
    // I. Next destination
    next_destination_address: "", next_destination_city: "", next_destination_state: "", next_destination_country: "",
    // J. Contact during India
    india_mobile_number: "", local_contact_number: "",
  });
  const [passport_pic, setPass] = useState(null);  // K — mandatory
  const [visa_pic, setVisa] = useState(null);      // K — mandatory
  const [flight_ticket, setTicket] = useState(null);
  const [self_pic, setSelf] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/participant/cform/mine").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  // Auto-fill from the participant's booking (name/nationality/arrival) so
  // they only need to fill the personal + document fields. Runs once on mount.
  useEffect(() => {
    api.get("/participant/me").then(({ data }) => {
      const b = data?.booking || {};
      const u = data?.user || {};
      // Split full name into given / surname on last space.
      const full = (b.customer || u.name || "").trim();
      const parts = full.split(" ");
      const surname = parts.length > 1 ? parts.slice(-1)[0] : "";
      const given = parts.length > 1 ? parts.slice(0, -1).join(" ") : full;
      setF((prev) => ({
        ...prev,
        surname: prev.surname || surname,
        given_names: prev.given_names || given,
        nationality: prev.nationality || b.nationality || data?.latest_cform?.nationality || "",
        date_of_birth: prev.date_of_birth || b.dob || "",
        email: prev.email || u.email || b.email || "",
        whatsapp_number: prev.whatsapp_number || b.whatsapp || u.phone || "",
        mobile_home_country: prev.mobile_home_country || b.phone || b.telephone || "",
        flight_number: prev.flight_number || b.arrival_flight_no || b.flight_number || "",
        arrival_airport: prev.arrival_airport || b.arrival_airport || "",
        arrival_city: prev.arrival_city || b.city || "",
        arrival_date: prev.arrival_date || (b.start_date || "").slice(0, 10),
        expected_checkout_date: prev.expected_checkout_date || (b.end_date || "").slice(0, 10),
        gender: prev.gender || b.gender || "",
      }));
    }).catch(() => {});
  }, []);

  const submit = async () => {
    if (!passport_pic || !visa_pic) { toast.error("Passport photo page and visa copy are mandatory"); return; }
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v ?? ""));
    fd.append("passport_pic", passport_pic);
    fd.append("visa_pic", visa_pic);
    if (self_pic) fd.append("self_pic", self_pic);
    if (flight_ticket) fd.append("flight_ticket", flight_ticket);
    try { await api.post("/participant/cform", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("C-form submitted to GEHM Jaipur"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    setBusy(false);
  };
  const del = async (id) => { await api.delete(`/participant/cform/${id}`); load(); };

  // Section/Field/UploadBox are called via their hoisted CF-prefixed
  // components below — avoids inline wrappers that would re-mount the
  // <input> on every keystroke and eat focus.

  return (
    <div className="space-y-4">
      {/* --------- Submitted C-Form gate --------- */}
      {rows.length > 0 ? (
        <SubmittedCFormLock rows={rows} onDelete={del}/>
      ) : (
      <>
      <div className="rounded-lg border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm flex items-start gap-2" data-testid="cform-mandatory">
        <ShieldAlert size={18} className="flex-shrink-0 mt-0.5"/>
        <div>
          <div className="font-semibold">C-Form submission is mandatory (India Foreigners Act).</div>
          <div className="text-xs mt-0.5">Fill each section — a few fields have been pre-filled from your booking. Passport photo page + visa copy are mandatory uploads.</div>
        </div>
      </div>
      <CFSection letter="A" title="Personal Information">
        <CFField f={f} setF={setF} k="surname" label="Surname (Last Name)" required/>
        <CFField f={f} setF={setF} k="given_names" label="Given Name(s)" required/>
        <CFField f={f} setF={setF} k="gender" label="Gender" options={["Male", "Female", "Other"]} required/>
        <CFField f={f} setF={setF} k="date_of_birth" label="Date of Birth" type="date" required/>
        <CFField f={f} setF={setF} k="nationality" label="Nationality" required/>
        <CFField f={f} setF={setF} k="special_category" label="Special Category (if applicable)"/>
      </CFSection>
      <CFSection letter="B" title="Permanent Address (Home Country)">
        <CFField f={f} setF={setF} k="home_address" label="Full Residential Address" span={2} required/>
        <CFField f={f} setF={setF} k="home_city" label="City" required/>
        <CFField f={f} setF={setF} k="home_state" label="State / Province"/>
        <CFField f={f} setF={setF} k="home_postal_code" label="Postal / ZIP Code"/>
        <CFField f={f} setF={setF} k="home_country" label="Country" required/>
      </CFSection>
      <CFSection letter="C" title="Contact Details">
        <CFField f={f} setF={setF} k="mobile_home_country" label="Mobile Number (Home Country)" required/>
        <CFField f={f} setF={setF} k="whatsapp_number" label="WhatsApp Number"/>
        <CFField f={f} setF={setF} k="email" label="Email Address" type="email" required/>
        <CFField f={f} setF={setF} k="emergency_contact_name" label="Emergency Contact Name" required/>
        <CFField f={f} setF={setF} k="emergency_contact_relationship" label="Relationship" required/>
        <CFField f={f} setF={setF} k="emergency_contact_number" label="Emergency Contact Number" required/>
      </CFSection>
      <CFSection letter="D" title="Passport Details">
        <CFField f={f} setF={setF} k="passport_no" label="Passport Number" required/>
        <CFField f={f} setF={setF} k="passport_issue_date" label="Issue Date" type="date" required/>
        <CFField f={f} setF={setF} k="passport_expiry_date" label="Expiry Date" type="date" required/>
        <CFField f={f} setF={setF} k="passport_place_of_issue" label="Place of Issue" required/>
        <div className="sm:col-span-2"><CFUploadBox file={passport_pic} setter={setPass} label="Upload Passport Photo Page" tid="cf-passport-pic" required/></div>
      </CFSection>
      <CFSection letter="E" title="Visa Details">
        <CFField f={f} setF={setF} k="visa_number" label="Visa Number" required/>
        <CFField f={f} setF={setF} k="visa_type" label="Visa Type" required/>
        <CFField f={f} setF={setF} k="visa_subtype" label="Visa Subtype"/>
        <CFField f={f} setF={setF} k="visa_issue_date" label="Issue Date" type="date" required/>
        <CFField f={f} setF={setF} k="visa_expiry_date" label="Expiry Date" type="date" required/>
        <CFField f={f} setF={setF} k="visa_place_of_issue" label="Place of Issue"/>
        <div className="sm:col-span-2"><CFUploadBox file={visa_pic} setter={setVisa} label="Upload Visa Copy / e-Visa PDF" tid="cf-visa-pic" required/></div>
      </CFSection>
      <CFSection letter="F" title="Travel Details">
        <CFField f={f} setF={setF} k="flight_number" label="Flight Number Arriving India" required/>
        <CFField f={f} setF={setF} k="arrival_airport" label="Arrival Airport in India" required/>
        <CFField f={f} setF={setF} k="arrival_city" label="Arrival City" required/>
        <CFField f={f} setF={setF} k="arrival_date" label="Arrival Date" type="date" required/>
        <CFField f={f} setF={setF} k="arrival_time" label="Arrival Time" type="time"/>
        <CFField f={f} setF={setF} k="country_of_departure" label="Country of Departure"/>
        <CFField f={f} setF={setF} k="city_of_departure" label="City of Departure"/>
      </CFSection>
      <CFSection letter="G" title="Arrival at Accommodation">
        <CFField f={f} setF={setF} k="checkin_date" label="Check-in Date" type="date"/>
        <CFField f={f} setF={setF} k="checkin_time" label="Check-in Time" type="time"/>
        <CFField f={f} setF={setF} k="expected_checkout_date" label="Expected Check-out Date" type="date"/>
        <CFField f={f} setF={setF} k="total_nights" label="Total Number of Nights" type="number"/>
        <CFField f={f} setF={setF} k="intended_duration_days" label="Intended Duration of Stay (Days)" type="number"/>
      </CFSection>
      <CFSection letter="H" title="Visit Information">
        <CFField f={f} setF={setF} k="purpose_of_visit" label="Purpose of Visit" required
               options={["Tourism", "Volunteer Program", "Internship", "Medical Elective", "Study", "Research", "Other"]}/>
        <CFField f={f} setF={setF} k="employed_in_india" label="Employed in India?" options={["No", "Yes"]}/>
      </CFSection>
      <CFSection letter="I" title="Next Destination">
        <CFField f={f} setF={setF} k="next_destination_address" label="Address" span={2}/>
        <CFField f={f} setF={setF} k="next_destination_city" label="City"/>
        <CFField f={f} setF={setF} k="next_destination_state" label="State"/>
        <CFField f={f} setF={setF} k="next_destination_country" label="Country"/>
      </CFSection>
      <CFSection letter="J" title="Contact During India">
        <CFField f={f} setF={setF} k="india_mobile_number" label="Indian Mobile Number"/>
        <CFField f={f} setF={setF} k="local_contact_number" label="Local Contact Number"/>
      </CFSection>
      <div className="border border-[color:var(--aii-border)] rounded-lg p-4 bg-white space-y-3">
        <div className="flex items-baseline gap-2">
          <span className="text-brand font-display font-bold text-lg">K.</span>
          <h3 className="font-semibold">Documents Upload</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <CFUploadBox file={flight_ticket} setter={setTicket} label="Flight Ticket" tid="cf-ticket"/>
          <CFUploadBox file={self_pic} setter={setSelf} label="Passport-size Photograph (optional)" tid="cf-self-pic"/>
        </div>
      </div>
      <button onClick={submit} disabled={busy} data-testid="cf-submit"
        className="w-full py-3 bg-brand text-white rounded font-semibold hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2"><Send size={14}/> {busy ? "Submitting…" : "Submit C-Form to GEHM Jaipur"}</button>
      </>
      )}
    </div>
  );
}

// Locked/collapsed view shown after the participant has submitted their
// C-Form. They see a summary card + can DELETE the submission to unlock
// the fillable wizard again — but they cannot double-submit, which was
// creating messy duplicate rows for GEHM.
function SubmittedCFormLock({ rows, onDelete }) {
  const [openId, setOpenId] = useState(null);
  const c = rows[0];  // most-recent submission (endpoint sorts DESC by submitted_at)
  const filed = c.status === "completed" || c.filed_date;
  const del = async () => {
    if (!window.confirm(
      "Delete your C-Form submission and start over?\n\n" +
      "GEHM Jaipur will no longer see your previous data. You'll have to fill and submit the whole form again."
    )) return;
    await onDelete(c.id);
  };
  return (
    <div className="space-y-3" data-testid="cform-locked">
      <div className={`rounded-lg border px-4 py-3 text-sm flex items-start gap-2 ${
        filed ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-brand bg-[color:var(--aii-green-tint)] text-[color:var(--aii-text-1)]"
      }`}>
        {filed ? <CheckCircle size={18} className="flex-shrink-0 mt-0.5 text-emerald-600"/> : <ShieldAlert size={18} className="flex-shrink-0 mt-0.5 text-brand"/>}
        <div className="flex-1">
          <div className="font-semibold">
            {filed
              ? "Your C-Form has been filed on FRRO."
              : "Your C-Form has been submitted to GEHM Jaipur."}
          </div>
          <div className="text-xs mt-0.5">
            {filed
              ? "You're all set — no further action required."
              : "GEHM will file it with the government portal shortly. To re-submit, delete this entry first."}
          </div>
        </div>
      </div>
      <div className="aii-card p-4" data-testid="cform-locked-card">
        <div className="flex items-start gap-3 flex-wrap">
          {(c.self_pic_url || c.passport_pic_url || c.passport_photo_url) && (
            <img src={c.self_pic_url || c.passport_pic_url || c.passport_photo_url}
                 alt="Submitted C-Form"
                 className="w-16 h-16 rounded-full object-cover border border-[color:var(--aii-border)] flex-shrink-0"/>
          )}
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Your C-Form on file</div>
            <div className="font-display font-semibold text-lg leading-tight mt-0.5">
              {[c.given_names, c.surname].filter(Boolean).join(" ") || c.participant_name || "(unnamed)"}
            </div>
            <div className="text-xs text-[color:var(--aii-muted)]">
              Passport <b className="text-[color:var(--aii-text-1)]">{c.passport_no || "—"}</b>
              {c.nationality ? <> · {c.nationality}</> : null}
              {c.arrival_date ? <> · arrival {(c.arrival_date || "").slice(0,10)}</> : null}
            </div>
            <div className="text-[11px] text-[color:var(--aii-muted)] mt-1">
              Submitted {c.submitted_at ? fmtDateTime(c.submitted_at) : "—"}
              {" · Status "}<b className={filed ? "text-emerald-700" : "text-brand"}>{filed ? "Filed on FRRO" : "Awaiting FRRO filing"}</b>
              {c.filed_by ? <> · Filed by {c.filed_by}</> : null}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setOpenId(openId === c.id ? null : c.id)}
                    data-testid="cform-toggle-details"
                    className="text-xs px-3 py-1.5 border border-brand text-brand rounded font-semibold hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
              {openId === c.id ? <><ChevronUp size={13}/> Hide details</> : <><ChevronDown size={13}/> View details</>}
            </button>
            <button onClick={del} disabled={filed}
                    title={filed ? "Already filed on FRRO — contact GEHM to re-submit" : "Delete this submission so you can re-fill the form"}
                    data-testid="cform-delete-locked"
                    className="text-xs px-3 py-1.5 border border-red-500 text-red-600 rounded font-semibold hover:bg-red-50 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
              <Trash2 size={13}/> Delete & re-submit
            </button>
          </div>
        </div>
        {openId === c.id && (
          <div className="mt-4 pt-4 border-t border-[color:var(--aii-border)] grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm" data-testid="cform-locked-details">
            {[
              ["Surname", c.surname],
              ["Given Names", c.given_names],
              ["Gender", c.gender],
              ["Date of Birth", c.date_of_birth],
              ["Nationality", c.nationality],
              ["Passport No", c.passport_no],
              ["Passport Expiry", c.passport_expiry_date],
              ["Visa Number", c.visa_number],
              ["Visa Type", c.visa_type],
              ["Flight Number", c.flight_number],
              ["Arrival Airport", c.arrival_airport],
              ["Arrival Date", c.arrival_date],
              ["Purpose of Visit", c.purpose_of_visit],
              ["Emergency Contact", c.emergency_contact_name],
              ["Emergency Number", c.emergency_contact_number],
              ["Home Address", c.home_address],
              ["Home City", c.home_city],
              ["Home Country", c.home_country],
            ].filter(([, v]) => v).map(([lbl, v]) => (
              <div key={lbl} className="flex items-start gap-2 border-b border-slate-100 py-1">
                <div className="text-[color:var(--aii-muted)] text-xs w-36 flex-shrink-0">{lbl}</div>
                <div className="font-medium text-[color:var(--aii-text-1)] break-words">{String(v)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ServicesPanel() {
  const [catalog, setCatalog] = useState([]);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const [{data:c},{data:m}] = await Promise.all([api.get("/service-catalog"), api.get("/service-requests")]);
    setCatalog(c); setMine(m);
  };
  useEffect(() => { load(); }, []);
  const req = async (s) => {
    const qty = s.free ? 1 : parseFloat(window.prompt(`Quantity for ${s.label} (${s.unit})?`, "1") || "1");
    if (!qty || qty <= 0) return;
    setBusy(true);
    try { await api.post("/service-requests", { service_id: s.id, quantity: qty }); toast.success("Request sent to GEHM"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const cancel = async (r) => {
    if (!window.confirm(`Cancel your request for ${r.service_label}?`)) return;
    try { await api.delete(`/service-requests/${r.id}`); toast.success("Request cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  return (
    <div className="space-y-4">
      <h2 className="font-display font-semibold text-lg">Request a service</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {catalog.map((s) => (
          <div key={s.id} className="aii-card p-4 flex items-center justify-between">
            <div><div className="font-medium">{s.label}</div><div className="text-xs text-[color:var(--aii-muted)]">{s.free ? "FREE" : `${USD(s.price)} ${s.unit}`}</div></div>
            <button onClick={()=>req(s)} disabled={busy} data-testid={`req-${s.key}`} className="text-sm px-3 py-1.5 bg-brand text-white rounded hover:bg-brand-dark">Request</button>
          </div>
        ))}
      </div>
      <h3 className="font-display font-semibold mt-4">My requests</h3>
      <div className="aii-card overflow-x-auto"><table className="aii-table"><thead><tr><th>Service</th><th>Qty</th><th>Total</th><th>Status</th><th>Payment</th><th></th></tr></thead><tbody>
        {mine.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-[color:var(--aii-muted)]">No requests yet.</td></tr>}
        {mine.map((r) => {
          // Participants can only cancel while the request is still pending and unpaid.
          const canCancel = (r.status === "pending" || r.status === "cancelled") && r.payment_status !== "paid";
          return (
            <tr key={r.id} data-testid={`svc-req-row-${r.id}`}>
              <td>{r.service_label}</td>
              <td>{r.quantity}</td>
              <td>{r.free ? "FREE" : USD(r.total)}</td>
              <td className="text-xs uppercase">{r.status}</td>
              <td className="text-xs uppercase">{r.payment_status}</td>
              <td className="text-right">
                {canCancel ? (
                  <button onClick={() => cancel(r)} data-testid={`svc-req-del-${r.id}`}
                    title="Cancel this pending request"
                    className="text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded inline-flex items-center gap-1">
                    <Trash2 size={11}/> Cancel
                  </button>
                ) : (
                  <span className="text-[10px] text-[color:var(--aii-muted)] italic" title="Once GEHM starts work or payment is captured, ask them to cancel for you">locked</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody></table></div>
    </div>
  );
}

function EventCard({ e, onAct }) {
  const imgs = (e.images && e.images.length > 0) ? e.images : (e.picture_url ? [e.picture_url] : []);
  const [hero, setHero] = useState(0);
  useEffect(() => { setHero(0); }, [e.id]);

  const step = (delta) => setHero((h) => (imgs.length ? (h + delta + imgs.length) % imgs.length : 0));

  const dateObj = e.date ? new Date(e.date) : null;
  const dayNum = dateObj && !isNaN(dateObj) ? dateObj.getDate() : null;
  const dayMon = dateObj && !isNaN(dateObj) ? dateObj.toLocaleString("en", { month: "short" }).toUpperCase() : null;

  const isBooked = e.my_action === "booked";
  const isSkipped = e.my_action === "not_interested";

  return (
    <div className="aii-card overflow-hidden flex flex-col shadow-sm hover:shadow-lg transition-shadow duration-300" data-testid={`ev-card-${e.id}`}>
      {/* Hero image with date badge & overlay */}
      <div className="relative">
        {imgs.length > 0 ? (
          <div className="aspect-[16/10] bg-slate-900 overflow-hidden group">
            <img src={imgs[hero]} alt={e.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none"/>
            {imgs.length > 1 && (
              <>
                <button onClick={()=>step(-1)} data-testid={`ev-prev-${e.id}`} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/85 hover:bg-white flex items-center justify-center shadow"><ChevronLeft size={16}/></button>
                <button onClick={()=>step(1)} data-testid={`ev-next-${e.id}`} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/85 hover:bg-white flex items-center justify-center shadow"><ChevronRight size={16}/></button>
              </>
            )}
          </div>
        ) : (
          <div className="aspect-[16/10] bg-gradient-to-br from-[color:var(--aii-green-tint)] to-slate-100 flex items-center justify-center text-[color:var(--aii-muted)]"><ImgIcon size={40}/></div>
        )}

        {/* Corner date ticket */}
        {dayNum && (
          <div className="absolute top-3 left-3 bg-white rounded shadow-lg overflow-hidden text-center" style={{ minWidth: 52 }}>
            <div className="bg-brand text-white text-[9px] font-bold tracking-widest px-2 py-0.5">{dayMon}</div>
            <div className="text-lg font-display font-bold px-2 py-1 leading-none">{dayNum}</div>
          </div>
        )}

        {/* Price/Free pill */}
        <div className="absolute top-3 right-3">
          {e.price ? (
            <span className="bg-white/95 backdrop-blur px-3 py-1 rounded-full text-sm font-display font-bold text-brand shadow">{USD(e.price)}</span>
          ) : (
            <span className="bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-bold tracking-widest shadow">FREE</span>
          )}
        </div>

        {/* Title overlay */}
        <div className="absolute bottom-0 inset-x-0 p-4 text-white">
          <div className="font-display text-xl sm:text-2xl font-bold leading-tight drop-shadow">{e.title}</div>
          {e.subtitle && <div className="text-xs opacity-90 mt-0.5 drop-shadow">{e.subtitle}</div>}
        </div>
      </div>

      {/* Thumbnail strip */}
      {imgs.length > 1 && (
        <div className="flex gap-1.5 px-3 pt-3 overflow-x-auto">
          {imgs.map((u, i) => (
            <button key={`${e.id}-thumb-${u.slice(-24)}-${i}`} onClick={()=>setHero(i)} data-testid={`ev-thumb-${e.id}-${i}`}
              className={`flex-shrink-0 w-14 h-14 rounded overflow-hidden border-2 transition ${i === hero ? "border-brand ring-2 ring-brand/30" : "border-transparent opacity-70 hover:opacity-100"}`}>
              <img src={u} alt="" className="w-full h-full object-cover"/>
            </button>
          ))}
        </div>
      )}

      <div className="p-4 space-y-3 flex-1 flex flex-col">
        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[color:var(--aii-text-2)]">
          {e.location && <span className="flex items-center gap-1"><MapPin size={12} className="text-brand"/> {e.location}</span>}
          {e.duration && <span className="flex items-center gap-1"><Clock size={12} className="text-brand"/> {e.duration}</span>}
        </div>

        {/* Description */}
        {e.description && <p className="text-sm text-[color:var(--aii-text-2)] whitespace-pre-wrap leading-relaxed">{e.description}</p>}

        {/* Itinerary timeline */}
        {(e.itinerary && e.itinerary.length > 0) && (
          <div className="border-t border-[color:var(--aii-border)] pt-3">
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2 flex items-center gap-1"><Sparkles size={11}/> Itinerary</div>
            <ol className="space-y-2">
              {e.itinerary.map((it, i) => (
                <li key={`${e.id}-itin-${i}-${(it.activity || '').slice(0,12)}`} className="flex gap-3 items-start" data-testid={`ev-itin-${e.id}-${i}`}>
                  <div className="flex flex-col items-center pt-0.5">
                    <div className="w-6 h-6 rounded-full bg-[color:var(--aii-green-tint)] text-brand flex items-center justify-center text-[10px] font-bold">{i + 1}</div>
                    {i < e.itinerary.length - 1 && <div className="w-px flex-1 bg-[color:var(--aii-border)] mt-0.5" style={{ minHeight: 12 }}/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-[color:var(--aii-muted)] font-semibold uppercase tracking-wider">
                      {it.day || ""}{it.day && it.time ? " · " : ""}{it.time || ""}
                    </div>
                    <div className="text-sm font-medium text-[color:var(--aii-text-1)]">{it.activity}</div>
                    {(it.leader_name || it.leader_type) && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[11px] bg-slate-50 border border-[color:var(--aii-border)] rounded-full px-2 py-0.5 text-[color:var(--aii-text-2)]">
                        <UserCheck size={10} className="text-brand"/>
                        <span className="font-semibold">{it.leader_name || "—"}</span>
                        {it.leader_type && <span className="text-[color:var(--aii-muted)]">· {it.leader_type}</span>}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Actions */}
        <div className="mt-auto pt-3 flex gap-2">
          <button onClick={()=>onAct(e.id, "booked")} data-testid={`ev-book-${e.id}`}
            className={`flex-1 text-sm px-3 py-2.5 rounded-lg font-semibold transition flex items-center justify-center gap-1.5 ${isBooked ? "bg-emerald-600 text-white shadow-lg shadow-emerald-500/30" : "bg-brand text-white hover:bg-brand-dark hover:shadow-md"}`}>
            {isBooked ? <><CheckCircle2 size={14}/> Booked</> : <>Reserve my spot</>}
          </button>
          <button onClick={()=>onAct(e.id, "not_interested")} data-testid={`ev-skip-${e.id}`}
            className={`text-sm px-3 py-2.5 rounded-lg transition ${isSkipped ? "bg-slate-200 text-slate-700" : "border border-[color:var(--aii-border)] hover:bg-slate-50 text-[color:var(--aii-muted)]"}`}>
            {isSkipped ? "Skipped" : "Skip"}
          </button>
        </div>

        {/* Booking count */}
        {(e.booked_names?.length > 0) && (
          <div className="text-[11px] text-[color:var(--aii-muted)] pt-1 border-t border-[color:var(--aii-border)]">
            <b className="text-brand">{e.booked_names.length}</b> already booked{e.booked_names.length <= 4 ? `: ${e.booked_names.join(", ")}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Lightbox — full-screen media viewer. Accepts a list of items
// ({type: 'image'|'video'|'youtube', src}) so the same component
// can preview uploaded stills, uploaded videos AND YouTube
// trailers with prev/next navigation. Close via Escape, backdrop
// tap, or the X button.
// -------------------------------------------------------------
function Lightbox({ items, index, onClose, onNav }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") onNav?.(1);
      if (e.key === "ArrowLeft") onNav?.(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNav]);
  const current = items?.[index];
  if (!current) return null;
  const stop = (e) => e.stopPropagation();
  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
         onClick={onClose} data-testid="image-lightbox">
      <button onClick={onClose} data-testid="lightbox-close"
              className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2 z-10">
        <XCircle size={22}/>
      </button>
      {items.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onNav?.(-1); }} data-testid="lightbox-prev"
                  className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 rounded-full p-2 z-10">
            <ChevronLeft size={26}/>
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNav?.(1); }} data-testid="lightbox-next"
                  className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 rounded-full p-2 z-10">
            <ChevronRight size={26}/>
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs bg-black/40 rounded-full px-3 py-1 z-10">
            {index + 1} / {items.length}
          </div>
        </>
      )}
      {current.type === "youtube" ? (
        <div className="w-full max-w-5xl aspect-video shadow-2xl rounded overflow-hidden bg-black" onClick={stop} data-testid="lightbox-youtube">
          <iframe title="video" src={`https://www.youtube.com/embed/${current.src}?autoplay=1`}
                  className="w-full h-full" allowFullScreen frameBorder="0"
                  allow="autoplay; encrypted-media; picture-in-picture"/>
        </div>
      ) : current.type === "video" ? (
        <video src={current.src} controls autoPlay
               className="max-h-[92vh] max-w-[92vw] shadow-2xl rounded bg-black"
               onClick={stop} data-testid="lightbox-video"/>
      ) : (
        <img src={current.src} alt=""
             className="max-h-[92vh] max-w-[92vw] object-contain shadow-2xl rounded"
             onClick={stop} data-testid="lightbox-image"/>
      )}
    </div>
  );
}

// -------------------------------------------------------------
// ActivitiesTabPanel — the participant's "Activities" tab (was
// "Events"). Renders every APPROVED activity scoped to their
// destination + city via /api/participant/activities. Same
// BookSkipCard + ItemDetailModal + Lightbox stack as the
// dashboard so participants have a consistent booking flow.
// -------------------------------------------------------------
function ActivitiesTabPanel() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");         // "" == show all
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState(null);         // {items:[{type,src}], index:0}
  const load = async () => {
    try {
      const { data } = await api.get("/participant/activities");
      setItems(data.activities || []);
      setCategories(data.categories || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const onDecide = async (item, decision) => {
    setBusy(item.id);
    try {
      await api.post(`/activities/${item.id}/decision`, { decision });
      toast.success(decision === "book" ? "Booked — see you there!" : "Noted, skipped");
      setSelected(prev => prev && prev.id === item.id ? { ...prev, my_decision: decision } : prev);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const openMedia = (item, idx) => setLightbox(openItemMedia(item, idx));
  const visible = activeCat ? items.filter(a => (a.category || "") === activeCat) : items;
  return (
    <div className="space-y-4" data-testid="activities-tab-panel">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Aii Group · Curated</div>
        <h2 className="font-display text-2xl font-semibold">Activities in your destination</h2>
        <p className="text-sm text-[color:var(--aii-muted)] mt-1">Everything approved for your city — tap a card for full details, media and pricing before you book.</p>
      </div>
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="cat-chip-row">
          <button onClick={() => setActiveCat("")}
                  data-testid="cat-chip-all"
                  className={`text-xs px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1.5 ${activeCat === "" ? "bg-brand text-white border-brand" : "bg-white border-[color:var(--aii-border)] hover:border-brand"}`}>
            <Sparkles size={12}/> All · {items.length}
          </button>
          {categories.map(c => {
            const count = items.filter(a => (a.category || "") === c).length;
            const Ico = CAT_ICON[c] || Sparkles;
            return (
              <button key={c} onClick={() => setActiveCat(c)}
                      data-testid={`cat-chip-${c.toLowerCase()}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1.5 ${activeCat === c ? "bg-brand text-white border-brand" : "bg-white border-[color:var(--aii-border)] hover:border-brand"}`}>
                <Ico size={12}/> {c} · {count}
              </button>
            );
          })}
        </div>
      )}
      {loading ? (
        <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]">Loading activities…</div>
      ) : visible.length === 0 ? (
        <div className="aii-card p-8 text-center">
          <CalendarClock size={32} className="mx-auto text-[color:var(--aii-muted)] mb-2"/>
          <div className="text-sm text-[color:var(--aii-muted)]">
            {activeCat ? `No ${activeCat} activities in your destination yet.` : "No activities lined up for your destination yet — new experiences drop here as they’re announced."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {visible.map(a => (
            <BookSkipCard key={a.id} item={a} kind="activity" busy={busy === a.id}
                          onDecide={onDecide} onOpen={setSelected}
                          onImageOpen={openMedia}
                          currencySymbol="$"/>
          ))}
        </div>
      )}
      {selected && (
        <ItemDetailModal item={selected} kind="activity" onClose={() => setSelected(null)}
                         onDecide={onDecide}
                         onImageOpen={openMedia}
                         currencySymbol="$"/>
      )}
      {lightbox && (
        <Lightbox items={lightbox.items} index={lightbox.index}
                  onClose={() => setLightbox(null)}
                  onNav={(dir) => setLightbox(l => l ? { ...l, index: (l.index + dir + l.items.length) % l.items.length } : l)}/>
      )}
    </div>
  );
}

function EventsPanel() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/events").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const act = async (eid, action) => { await api.post(`/events/${eid}/action`, { action }); toast.success(action === "booked" ? "You're in! See you there 🎉" : "Noted"); load(); };
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Aii Group · Curated</div>
        <h2 className="font-display text-2xl font-semibold">Upcoming events</h2>
        <p className="text-sm text-[color:var(--aii-muted)] mt-1">Handpicked experiences to make your India journey unforgettable.</p>
      </div>
      {rows.length === 0 && (
        <div className="aii-card p-8 text-center">
          <CalendarClock size={32} className="mx-auto text-[color:var(--aii-muted)] mb-2"/>
          <div className="text-sm text-[color:var(--aii-muted)]">No events yet — check back soon.</div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {rows.map((e) => <EventCard key={e.id} e={e} onAct={act}/>)}
      </div>
    </div>
  );
}

function ThreadPanel({ kind, title, targetRole, placeholder, submitLabel }) {
  const [rows, setRows] = useState([]);
  const [subject, setSubject] = useState("");
  const [comment, setComment] = useState("");
  const load = () => api.get(`/threads?kind=${kind}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [kind]);
  const submit = async () => {
    if (!subject.trim()) return toast.error("Subject required");
    try { await api.post("/threads", { kind, subject: subject.trim(), comment: comment.trim() }); toast.success(`Sent to ${targetRole}`); setSubject(""); setComment(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const rate = async (id, rating) => {
    const { data } = await api.post(`/threads/${id}/rate`, { rating });
    toast.success(data.escalated ? "Escalated to BDOM" : "Thanks for rating");
    load();
  };
  const del = async (id) => { await api.delete(`/threads/${id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="aii-card p-5 space-y-3">
        <h2 className="font-display font-semibold text-lg">{title}</h2>
        {kind === "feedback" && (
          <p className="text-sm text-[color:var(--aii-text-2)]">We&rsquo;d love to hear your suggestions! Anything that could make the experience better — food, tours, workshops, communication — drop it here and our BDOM &amp; Founder will pick it up.</p>
        )}
        <input className="input-aii w-full" placeholder="Subject" value={subject} onChange={(e)=>setSubject(e.target.value)} data-testid={`th-${kind}-subject`}/>
        <textarea className="input-aii w-full" rows={3} placeholder={placeholder || "Add a comment (optional)"} value={comment} onChange={(e)=>setComment(e.target.value)}/>
        <button onClick={submit} data-testid={`th-${kind}-submit`} className="px-4 py-2 bg-brand text-white rounded font-semibold hover:bg-brand-dark flex items-center gap-2"><Send size={14}/> {submitLabel || "Submit"}</button>
      </div>
      {rows.map((t) => (
        <div key={t.id} className="aii-card p-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0"><div className="font-medium">{t.subject}</div><div className="text-xs text-[color:var(--aii-muted)]">{fmtDateTime(t.created_at)} · <b>{t.status}</b>{t.escalated_to_bdom && <span className="ml-1 text-red-700">· escalated</span>}</div></div>
            <button onClick={()=>del(t.id)} className="text-red-600 p-1"><Trash2 size={14}/></button>
          </div>
          <div className="mt-3 space-y-2">
            {t.messages?.map((m,i) => (
              <div key={i} className={`text-sm p-2 rounded ${m.by === "staff" ? "bg-[color:var(--aii-green-tint)]" : "bg-slate-50"}`}>
                <div className="text-xs text-[color:var(--aii-muted)]">{m.by === "staff" ? `${m.by_name} (${m.by_role})` : "You"} · {fmtDateTime(m.at)}</div>
                <div>{m.text}</div>
              </div>
            ))}
          </div>
          {t.status === "awaiting_rating" && (
            <div className="mt-3 pt-3 border-t border-[color:var(--aii-border)]">
              <div className="text-xs text-[color:var(--aii-muted)] mb-1">Rate the response (0 = worst, 5 = perfect)</div>
              <div className="flex gap-1">
                {[0,1,2,3,4,5].map((n) => <button key={n} onClick={()=>rate(t.id,n)} data-testid={`th-${kind}-rate-${t.id}-${n}`} className="w-9 h-9 rounded border border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)] flex items-center justify-center"><Star size={13}/> {n}</button>)}
              </div>
            </div>
          )}
          {t.rating !== null && t.rating !== undefined && <div className="mt-2 text-xs">Your rating: <b className="text-brand">{t.rating}/5</b></div>}
        </div>
      ))}
    </div>
  );
}

function DisclaimerPanel() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/threads?kind=disclaimer").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const sign = async (accept) => {
    await api.post("/threads", { kind: "disclaimer", subject: `Photo & Video Consent — ${accept ? "ACCEPT" : "REJECT"}`, comment: accept ? "I consent to Aii Group using my photos & videos in marketing." : "I do NOT consent to Aii Group using my photos & videos.", accept });
    toast.success(accept ? "Consent recorded" : "Rejection recorded");
    load();
  };
  const hasSigned = rows.length > 0;
  return (
    <div className="space-y-4">
      {!hasSigned && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm flex items-start gap-2" data-testid="consent-mandatory">
          <ShieldAlert size={18} className="flex-shrink-0 mt-0.5"/>
          <div>
            <div className="font-semibold">Consent form is mandatory.</div>
            <div className="text-xs mt-0.5">Please record your decision below — Accept or Reject. Either response is acceptable; we just need it on record before your programme starts.</div>
          </div>
        </div>
      )}
      <div className="aii-card p-5 space-y-3">
        <h2 className="font-display font-semibold text-lg">Photo &amp; Video Consent</h2>
        <p className="text-sm text-[color:var(--aii-text-2)]">Aii Group would love to use the memories captured during your programme (photos + video testimonials) on our website, social media and partner decks. Do you consent?</p>
        <div className="flex gap-2">
          <button onClick={()=>sign(true)} data-testid="disc-accept" className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700 flex items-center justify-center gap-2"><CheckCircle2 size={16}/> Accept</button>
          <button onClick={()=>sign(false)} data-testid="disc-reject" className="flex-1 px-4 py-3 border border-red-200 text-red-700 rounded font-semibold hover:bg-red-50 flex items-center justify-center gap-2"><XCircle size={16}/> Reject</button>
        </div>
      </div>
      {rows.map((t) => <div key={t.id} className="aii-card p-3 text-sm"><b>{t.subject}</b> · {fmtDateTime(t.created_at)}</div>)}
    </div>
  );
}

// ---------------- Pre-arrival Instructions ----------------
function InstructionsPanel() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/pre-arrival-docs")
      .then(({ data }) => setDocs(data || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load instructions"))
      .finally(() => setLoading(false));
  }, []);

  // Popup-blocker safe download — browsers block `window.open()` after
  // an awaited fetch because the user gesture is lost. Open the tab
  // synchronously on the click, then swap its URL to the blob once the
  // file is ready. Falls back to programmatic `<a download>` if the
  // popup was blocked entirely (in-app webviews / strict browsers).
  const openDoc = async (docId, filename) => {
    const win = window.open("", "_blank", "noopener");
    if (win) {
      // Safe DOM API (createElement + textContent) instead of the
      // document.write() sink flagged by the code reviewer.
      try {
        win.document.title = "Loading…";
        const div = win.document.createElement("div");
        div.textContent = "Fetching document…";
        div.style.cssText = "font:14px system-ui;color:#555;padding:24px";
        win.document.body.appendChild(div);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("InstructionsPanel: could not paint loading placeholder", e);
        }
      }
    }
    try {
      const resp = await fetch(`${API}/pre-arrival-docs/${docId}/file`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = filename || "document";
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      api.post(`/pre-arrival-docs/${docId}/view`).catch(() => {});
    } catch (e) {
      if (win && !win.closed) win.close();
      toast.error("Couldn't open — please try again.");
      if (process.env.NODE_ENV !== "production") console.warn("InstructionsPanel: open failed", e);
    }
  };

  const fmtSize = (b) => b > 1_000_000 ? `${(b/1_000_000).toFixed(1)} MB` : b > 1000 ? `${Math.round(b/1000)} KB` : `${b} B`;
  const iconFor = (ct) => (ct || "").includes("pdf") ? "📄" : (ct || "").includes("presentation") || (ct||"").includes("powerpoint") ? "📊" : "📎";
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">From your BDOM</div>
        <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><BookOpen size={20} className="text-brand"/> Pre-arrival Instructions</h2>
        <p className="text-sm text-[color:var(--aii-muted)] mt-1">Everything you need to prepare before you land — packing lists, cultural orientation, medical prep. Download and read at your own pace.</p>
      </div>
      {loading ? (
        <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="aii-card p-8 text-center">
          <BookOpen size={32} className="mx-auto text-[color:var(--aii-muted)] mb-2"/>
          <div className="text-sm text-[color:var(--aii-muted)]">Your BDOM hasn&rsquo;t uploaded pre-arrival documents yet — check back soon.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {docs.map((d) => {
            const scoped = Array.isArray(d.partner_ids) && d.partner_ids.length > 0;
            const scopeToCity = d.city ? ` · ${d.city}` : "";
            const isYt = d.kind === "youtube";
            const ytMatch = isYt ? (d.youtube_url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/) : null;
            const ytId = ytMatch ? ytMatch[1] : null;
            // For YouTube docs we render an in-card iframe player so
            // participants never leave the portal to watch. File docs
            // stay as a click-to-download card.
            if (isYt && ytId) {
              return (
                <div key={d.id} data-testid={`prearrival-${d.id}`}
                     className={`aii-card overflow-hidden ${scoped ? "border-brand/40" : ""}`}>
                  <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                    <iframe
                      src={`https://www.youtube.com/embed/${ytId}?rel=0`}
                      title={d.title}
                      data-testid={`prearrival-embed-${d.id}`}
                      onLoad={() => { api.post(`/pre-arrival-docs/${d.id}/view`).catch(() => {}); }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full border-0"/>
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-semibold text-base leading-tight">{d.title}</div>
                      <span data-testid={`prearrival-scope-${d.id}`}
                            title={scoped ? "Sent just to your partner account" : "Sent to every partner"}
                            className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${scoped ? "bg-brand text-white" : "bg-slate-100 text-[color:var(--aii-muted)]"}`}>
                        {scoped ? `For you${scopeToCity}` : "Global"}
                      </span>
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">YouTube</span>
                    </div>
                    <div className="text-[10px] text-[color:var(--aii-muted)] mt-1">Uploaded by {d.uploaded_by_name}</div>
                  </div>
                </div>
              );
            }
            return (
            <button key={d.id} data-testid={`prearrival-${d.id}`}
               onClick={() => openDoc(d.id, d.filename)}
               className={`aii-card p-4 flex items-start gap-3 hover:border-brand hover:shadow-md transition group text-left w-full ${scoped ? "border-brand/40" : ""}`}>
              <div className="text-3xl flex-shrink-0">{iconFor(d.content_type)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-semibold text-base leading-tight group-hover:text-brand">{d.title}</div>
                  <span data-testid={`prearrival-scope-${d.id}`}
                        title={scoped ? "Sent just to your partner account" : "Sent to every partner"}
                        className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${scoped ? "bg-brand text-white" : "bg-slate-100 text-[color:var(--aii-muted)]"}`}>
                    {scoped ? `For you${scopeToCity}` : "Global"}
                  </span>
                </div>
                <div className="text-[11px] text-[color:var(--aii-muted)] mt-1 truncate">{d.filename} · {fmtSize(d.size)}</div>
                <div className="text-[10px] text-brand font-semibold mt-0.5 flex items-center gap-1"><Download size={9}/> Tap to open in new tab & download</div>
              </div>
              <Download size={16} className="text-brand flex-shrink-0 opacity-70 group-hover:opacity-100"/>
            </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------- Key Contacts ----------------
function ContactsPanel() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/participant/key-contacts")
      .then(({ data }) => setContacts(data || []))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  }, []);

  const setPreferred = async (c) => {
    if (c.is_emergency) return; // Founder is the emergency contact — not a routine reply-to
    setBusy(true);
    const nextId = c.is_preferred ? null : c.id;
    try {
      await api.post("/participant/key-contacts/prefer", { user_id: nextId });
      setContacts((prev) => prev.map((x) => ({ ...x, is_preferred: nextId ? x.id === nextId : false })));
      toast.success(nextId ? `${c.name} is now your first-line contact` : "First-line contact cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const telHref = (p) => p ? `tel:${p.replace(/[^+\d]/g, "")}` : null;
  const waHref = (p) => p ? `https://wa.me/${p.replace(/[^\d]/g, "")}` : null;
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Your team on the ground</div>
        <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><Phone size={20} className="text-brand"/> Key Contact Persons</h2>
        <p className="text-sm text-[color:var(--aii-muted)] mt-1">Save these numbers — reach out any time you need help during your programme.</p>
      </div>
      {loading ? (
        <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]">Loading…</div>
      ) : contacts.length === 0 ? (
        <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]">No contacts published yet — please ask your BDOM.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {contacts.map((c) => (
            <div key={c.role} data-testid={`contact-${c.role.toLowerCase()}`}
                 className={`aii-card p-5 flex items-start gap-4 ${c.is_emergency ? "border-red-300 bg-red-50/40" : ""} ${c.is_preferred ? "ring-2 ring-brand" : ""}`}>
              {c.profile_pic_id ? (
                <div className="relative flex-shrink-0">
                  <img src={`${API}/files/${c.profile_pic_id}?auth=${getToken()}`} alt={c.name}
                       className="w-16 h-16 rounded-full object-cover border-2 border-[color:var(--aii-green-tint)]"/>
                  <span data-testid={`contact-${c.role.toLowerCase()}-reach`}
                        title={c.reachable ? "Phone + email on file" : "Missing phone or email — reach out to Founder"}
                        className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white ${c.reachable ? "bg-emerald-500" : "bg-gray-300"}`}/>
                </div>
              ) : (
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-bold flex items-center justify-center font-display text-xl">
                    {(c.name || "?")[0]}
                  </div>
                  <span data-testid={`contact-${c.role.toLowerCase()}-reach`}
                        title={c.reachable ? "Phone + email on file" : "Missing phone or email — reach out to Founder"}
                        className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white ${c.reachable ? "bg-emerald-500" : "bg-gray-300"}`}/>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className={`text-[10px] uppercase tracking-widest font-bold ${c.is_emergency ? "text-red-700" : "text-brand"}`}>
                    {c.is_emergency ? "Emergency Contact" : c.title.split(" (")[0]}
                  </div>
                  {!c.is_emergency && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setPreferred(c)}
                      data-testid={`contact-${c.role.toLowerCase()}-prefer`}
                      title={c.is_preferred
                        ? "Currently your first-line contact — tap to clear"
                        : "Tap to make this person your default reply-to for Suggestions, Services & Projects"}
                      className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 transition ${
                        c.is_preferred ? "bg-brand text-white" : "text-[color:var(--aii-muted)] hover:text-brand"
                      }`}>
                      <Star size={11} className={c.is_preferred ? "fill-current" : ""}/> {c.is_preferred ? "Preferred" : "Prefer"}
                    </button>
                  )}
                </div>
                <div className="font-display font-semibold text-base leading-tight mt-0.5 flex items-center gap-1.5 flex-wrap">
                  {c.name}
                  {c.is_local && !c.is_emergency && (
                    <span className="pill pill-green text-[9px]" data-testid={`contact-${c.role.toLowerCase()}-local`}>Local · {c.office}</span>
                  )}
                </div>
                <div className="text-xs text-[color:var(--aii-muted)] mt-0.5 truncate">{c.title}</div>
                {c.phone ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <a href={telHref(c.phone)} data-testid={`contact-${c.role.toLowerCase()}-call`}
                       className="text-xs px-2.5 py-1 bg-brand text-white rounded-md hover:bg-brand-dark flex items-center gap-1"><Phone size={11}/> Call</a>
                    <a href={waHref(c.phone)} target="_blank" rel="noopener noreferrer" data-testid={`contact-${c.role.toLowerCase()}-wa`}
                       className="text-xs px-2.5 py-1 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">WhatsApp</a>
                    <span className="text-[11px] text-[color:var(--aii-muted)] w-full mt-0.5" data-testid={`contact-${c.role.toLowerCase()}-phone`}>{c.phone}</span>
                  </div>
                ) : (
                  <div className="mt-2 text-[11px] text-[color:var(--aii-muted)] italic">No phone on file — ask your BDOM.</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Root layout ----------------

function ytEmbedId(url) { const m = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/); return m ? m[1] : null; }

function VideosPanel() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/prearrival-videos").then(({data}) => setVideos(data || [])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">From your BDOM</div>
        <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><Youtube size={22} className="text-brand"/> Pre-arrival videos</h2>
        <p className="text-sm text-[color:var(--aii-muted)] mt-1">Watch these before you arrive — curated for your destination &amp; project.</p>
      </div>
      {loading ? <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]">Loading…</div> :
        videos.length === 0 ? <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]">No videos yet.</div> :
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {videos.map(v => {
            const id = ytEmbedId(v.youtube_url);
            return (
              <div key={v.id} className="aii-card overflow-hidden" data-testid={`pv-${v.id}`}>
                {id ? (
                  <div className="aspect-video bg-black">
                    <iframe src={`https://www.youtube.com/embed/${id}`} title={v.title} className="w-full h-full" allowFullScreen frameBorder="0"/>
                  </div>
                ) : <a href={v.youtube_url} target="_blank" rel="noopener noreferrer" className="aspect-video bg-slate-100 flex items-center justify-center"><Youtube size={40} className="text-brand"/></a>}
                <div className="p-3">
                  <div className="font-semibold text-sm flex items-center gap-2">{v.title} {v.mandatory && <span className="text-[9px] uppercase tracking-widest bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Mandatory</span>}</div>
                  {v.description && <div className="text-xs text-[color:var(--aii-muted)] mt-0.5">{v.description}</div>}
                </div>
              </div>
            );
          })}
        </div>
      }
    </div>
  );
}

function AttendancePanel({ user }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ date: new Date().toISOString().slice(0,10), status: "present", hours: 8, leave_reason: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [backfillPrompt, setBackfillPrompt] = useState(null); // { date, status, hours, leave_reason }
  const [backfillReason, setBackfillReason] = useState("");
  const load = async () => { const { data } = await api.get("/participant-attendance"); setRows(data || []); };
  useEffect(() => { load(); }, []);
  const isPastDate = (d) => (d || "").slice(0,10) < new Date().toISOString().slice(0,10);
  const submit = async () => {
    if (!f.date) return toast.error("Pick a date");
    if (f.status === "leave" && !f.leave_reason.trim()) return toast.error("Leave reason required");
    // Client-side gate: if the participant picked a past date, jump
    // straight to the backfill-request flow instead of hitting the
    // API and eating a 403. Keeps the UX one-tap.
    if (isPastDate(f.date)) {
      setBackfillPrompt({ ...f, hours: parseFloat(f.hours) || 0 });
      setBackfillReason("");
      return;
    }
    setBusy(true);
    try { await api.post("/participant-attendance", { ...f, hours: parseFloat(f.hours) || 0 }); toast.success("Logged"); await load(); }
    catch (e) {
      // Fallback: server-side lock triggers (clock skew, DST edges) — surface
      // the same backfill modal so participant never hits a dead end.
      const msg = e?.response?.data?.detail || "Failed";
      if ((e?.response?.status === 403) && /locked/i.test(msg)) {
        setBackfillPrompt({ ...f, hours: parseFloat(f.hours) || 0 });
        setBackfillReason("");
      } else {
        toast.error(msg);
      }
    }
    finally { setBusy(false); }
  };
  const submitBackfill = async () => {
    if (!backfillPrompt) return;
    if (!backfillReason.trim()) { toast.error("Please give your PCWM a short reason"); return; }
    setBusy(true);
    try {
      await api.post("/participant-attendance/request-backfill", {
        date: backfillPrompt.date,
        status: backfillPrompt.status,
        hours: backfillPrompt.hours,
        leave_reason: backfillPrompt.leave_reason,
        reason: backfillReason.trim(),
      });
      toast.success("Backfill request sent to your PCWM");
      setBackfillPrompt(null);
      setBackfillReason("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const totalHours = rows.filter(r => r.status === "present").reduce((s, r) => s + (r.hours || 0), 0);
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Your log</div>
        <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><ClipboardCheck size={22} className="text-brand"/> Attendance &amp; volunteer hours</h2>
        <p className="text-sm text-[color:var(--aii-muted)] mt-1">Log your working days + hours (or leave with a reason). Your BDOM &amp; PCWM see this list live.</p>
      </div>
      <div className="aii-card p-4 space-y-2" data-testid="attendance-form">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <input type="date" className="input-aii" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} data-testid="att-date"/>
          <select className="input-aii" value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="att-status">
            <option value="present">Present</option><option value="leave">Leave</option>
          </select>
          {f.status === "present" ? (
            <input className="input-aii" type="number" step="0.5" min="0" max="24" value={f.hours} onChange={e => setF({ ...f, hours: e.target.value })} placeholder="Hours" data-testid="att-hours"/>
          ) : (
            <input className="input-aii sm:col-span-2" value={f.leave_reason} onChange={e => setF({ ...f, leave_reason: e.target.value })} placeholder="Reason for leave" data-testid="att-reason"/>
          )}
          <button onClick={submit} disabled={busy} data-testid="att-submit" className="bg-brand text-white font-semibold px-3 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50">{busy ? "…" : <><Plus size={13} className="inline"/> Log</>}</button>
        </div>
        <input className="input-aii" placeholder="Notes (optional)" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })}/>
      </div>
      <div className="aii-card p-4">
        <div className="text-sm text-[color:var(--aii-text-2)] mb-2">Total volunteer hours: <b className="text-brand">{totalHours.toFixed(1)}h</b> · {rows.filter(r => r.status === "present").length} days present · {rows.filter(r => r.status === "leave").length} leave</div>
        <table className="aii-table text-sm">
          <thead><tr><th>Date</th><th>Status</th><th>Hours</th><th>Reason / Notes</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-[color:var(--aii-muted)]">Nothing logged yet.</td></tr>}
            {rows.map(r => (
              <tr key={r.id}><td>{r.date}</td><td className="uppercase text-xs">{r.status}</td><td>{r.hours || "—"}</td><td className="text-xs">{r.leave_reason || r.notes || "—"}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      {backfillPrompt && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
             onClick={() => setBackfillPrompt(null)}
             data-testid="participant-backfill-backdrop">
          <div className="bg-white rounded-xl max-w-md w-full overflow-hidden"
               onClick={(e) => e.stopPropagation()}
               data-testid="participant-backfill-modal">
            <div className="p-5 border-b border-[color:var(--aii-border)]">
              <div className="text-xs uppercase tracking-widest text-amber-700 font-bold">
                Past date locked
              </div>
              <h3 className="text-lg font-display font-semibold mt-1">Request a backfill</h3>
              <p className="text-xs text-[color:var(--aii-muted)] mt-1">
                Volunteer hours for <b>{backfillPrompt.date}</b> can only be logged by your Placement
                Manager. Send them a short reason and they'll approve it on your behalf.
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs">
                <div><b>Date:</b> {backfillPrompt.date}</div>
                <div><b>Status:</b> {backfillPrompt.status.toUpperCase()}</div>
                {backfillPrompt.status === "present" && <div><b>Hours:</b> {backfillPrompt.hours || "—"}</div>}
                {backfillPrompt.status === "leave" && <div><b>Leave reason:</b> {backfillPrompt.leave_reason || "—"}</div>}
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)]">
                  Why do you need this backfilled?
                </label>
                <textarea
                  data-testid="participant-backfill-reason"
                  value={backfillReason}
                  onChange={(e) => setBackfillReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Was at placement but forgot to open the app"
                  className="input-aii w-full mt-1 text-sm"
                  autoFocus
                />
              </div>
            </div>
            <div className="p-4 border-t border-[color:var(--aii-border)] flex justify-end gap-2 bg-slate-50/50">
              <button
                onClick={() => { setBackfillPrompt(null); setBackfillReason(""); }}
                disabled={busy}
                data-testid="participant-backfill-cancel"
                className="px-3 py-1.5 text-sm">Cancel</button>
              <button
                data-testid="participant-backfill-send"
                onClick={submitBackfill}
                disabled={busy || !backfillReason.trim()}
                className="bg-brand text-white font-semibold px-4 py-1.5 rounded-md hover:bg-brand-dark disabled:opacity-50 text-sm">
                {busy ? "Sending…" : "Send to PCWM"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Destination context ----------------
// Weather code lookup for Open-Meteo — https://open-meteo.com/en/docs
const WEATHER_CODES = {
  0: { label: "Clear sky", icon: "☀️" },
  1: { label: "Mainly clear", icon: "🌤️" },
  2: { label: "Partly cloudy", icon: "⛅" },
  3: { label: "Overcast", icon: "☁️" },
  45: { label: "Fog", icon: "🌫️" },
  48: { label: "Rime fog", icon: "🌫️" },
  51: { label: "Light drizzle", icon: "🌦️" },
  53: { label: "Drizzle", icon: "🌦️" },
  55: { label: "Heavy drizzle", icon: "🌧️" },
  61: { label: "Light rain", icon: "🌦️" },
  63: { label: "Rain", icon: "🌧️" },
  65: { label: "Heavy rain", icon: "🌧️" },
  71: { label: "Light snow", icon: "🌨️" },
  73: { label: "Snow", icon: "❄️" },
  75: { label: "Heavy snow", icon: "❄️" },
  80: { label: "Rain showers", icon: "🌦️" },
  81: { label: "Heavy showers", icon: "🌧️" },
  82: { label: "Violent showers", icon: "⛈️" },
  95: { label: "Thunderstorm", icon: "⛈️" },
  96: { label: "Storm w/ hail", icon: "⛈️" },
  99: { label: "Severe storm", icon: "⛈️" },
};
const wxCode = (c) => WEATHER_CODES[c] || { label: "—", icon: "🌡️" };

function WeatherCard({ country, city }) {
  const [wx, setWx] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const params = country && city ? `?country=${encodeURIComponent(country)}&city=${encodeURIComponent(city)}` : "";
    api.get(`/destination-weather${params}`)
      .then(({ data }) => setWx(data))
      .catch(() => setWx({ available: false }))
      .finally(() => setLoading(false));
  }, [country, city]);

  if (loading) return <div className="aii-card p-5 text-sm text-[color:var(--aii-muted)]" data-testid="wx-loading">Fetching weather…</div>;
  if (!wx?.available) {
    return (
      <div className="aii-card p-5" data-testid="wx-unavailable">
        <div className="flex items-center gap-2 text-[color:var(--aii-muted)] text-sm">
          <Cloud size={18}/> Weather unavailable for this destination right now.
        </div>
      </div>
    );
  }
  const c = wx.current || {};
  const daily = wx.daily || {};
  const days = (daily.time || []).slice(0, 7);
  const cur = wxCode(c.weather_code);
  return (
    <div className="aii-card p-5" data-testid="wx-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1"><Cloud size={11}/> Live weather · Open-Meteo</div>
          <h3 className="font-display font-semibold text-lg">{wx.resolved?.name || city}, {wx.resolved?.country || country}</h3>
        </div>
        <div className="text-right">
          <div className="text-4xl">{cur.icon}</div>
        </div>
      </div>
      <div className="flex items-end gap-4 mb-3">
        <div className="text-5xl font-display font-bold text-brand" data-testid="wx-temp">{Math.round(c.temperature_2m ?? 0)}°C</div>
        <div className="text-sm text-[color:var(--aii-text-2)] pb-1">
          <div className="font-semibold">{cur.label}</div>
          <div className="text-[11px] text-[color:var(--aii-muted)]">Humidity {c.relative_humidity_2m ?? "—"}% · Wind {c.wind_speed_10m ?? "—"} km/h</div>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 pt-3 border-t border-[color:var(--aii-border)]">
        {days.map((d, i) => {
          const w = wxCode(daily.weather_code?.[i]);
          const dt = new Date(d);
          const dow = dt.toLocaleDateString("en", { weekday: "short" });
          return (
            <div key={d} className="text-center" data-testid={`wx-day-${i}`}>
              <div className="text-[10px] text-[color:var(--aii-muted)] font-semibold uppercase">{dow}</div>
              <div className="text-xl">{w.icon}</div>
              <div className="text-[11px] font-semibold">{Math.round(daily.temperature_2m_max?.[i] ?? 0)}°</div>
              <div className="text-[10px] text-[color:var(--aii-muted)]">{Math.round(daily.temperature_2m_min?.[i] ?? 0)}°</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HolidaysCard({ country, city }) {
  const [state, setState] = useState({ loading: true, holidays: [], available: false });
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  useEffect(() => {
    const params = country && city ? `?country=${encodeURIComponent(country)}&city=${encodeURIComponent(city)}` : "";
    api.get(`/destination-holidays${params}`)
      .then(({ data }) => setState({ loading: false, ...data }))
      .catch(() => setState({ loading: false, holidays: [], available: false }));
    api.get("/participant/holiday-notifications")
      .then(({ data }) => setNotifyEnabled(!!data.enabled))
      .catch(() => {});
  }, [country, city]);

  const toggleNotify = async () => {
    const next = !notifyEnabled;
    setNotifyBusy(true);
    try {
      await api.post("/participant/holiday-notifications", { enabled: next });
      setNotifyEnabled(next);
      toast.success(next ? "You'll get a heads-up 3 days before each holiday" : "Holiday reminders off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setNotifyBusy(false); }
  };

  if (state.loading) return null;
  if (!state.available || state.holidays.length === 0) {
    return (
      <div className="aii-card p-5" data-testid="dest-holidays-empty">
        <div className="flex items-center gap-2 mb-2">
          <PartyPopper size={16} className="text-brand"/>
          <h3 className="font-display font-semibold text-lg">Public holidays</h3>
        </div>
        <p className="text-sm text-[color:var(--aii-muted)]">No public holidays in {country} in the next 6 months — a rare stretch of business-as-usual!</p>
      </div>
    );
  }
  // Bucket by month for a nicer visual grouping.
  const bucketed = state.holidays.reduce((acc, h) => {
    const d = new Date(h.date);
    const key = d.toLocaleString("en", { month: "long", year: "numeric" });
    (acc[key] = acc[key] || []).push({ ...h, d });
    return acc;
  }, {});

  return (
    <div className="aii-card p-5" data-testid="dest-holidays">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <PartyPopper size={16} className="text-brand"/>
          <h3 className="font-display font-semibold text-lg">Public holidays · next 6 months</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">{state.count} coming up</span>
          <button onClick={toggleNotify} disabled={notifyBusy}
            data-testid="holiday-notify-toggle"
            title={notifyEnabled ? "Turn off holiday reminders" : "Ping me 3 days before each holiday"}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition flex items-center gap-1 ${notifyEnabled ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-[color:var(--aii-text-2)] border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"} disabled:opacity-60`}>
            <Bell size={11}/> {notifyEnabled ? "Alerts on · 3 days ahead" : "Notify me 3 days before"}
          </button>
        </div>
      </div>
      <div className="space-y-4">
        {Object.entries(bucketed).map(([month, items]) => (
          <div key={month}>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2 pb-1 border-b border-[color:var(--aii-border)]">{month}</div>
            <ul className="space-y-2">
              {items.map((h, i) => (
                <li key={`${h.date}-${i}`} className="flex items-start gap-3" data-testid={`dest-holiday-${h.date}`}>
                  <div className="bg-[color:var(--aii-green-tint)] text-brand rounded-md text-center overflow-hidden flex-shrink-0" style={{ minWidth: 44 }}>
                    <div className="text-[9px] font-bold tracking-widest bg-brand text-white px-1 py-0.5 uppercase">{h.d.toLocaleString("en", { month: "short" })}</div>
                    <div className="text-lg font-display font-bold px-2 py-0.5 leading-none">{h.d.getDate()}</div>
                    <div className="text-[9px] font-semibold text-[color:var(--aii-muted)] px-1 pb-0.5">{h.d.toLocaleString("en", { weekday: "short" }).toUpperCase()}</div>
                  </div>
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="font-semibold text-sm">{h.name}</div>
                    {h.note && <div className="text-xs text-[color:var(--aii-text-2)] mt-0.5">{h.note}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-3 text-[11px] text-[color:var(--aii-muted)] bg-slate-50 border border-[color:var(--aii-border)] rounded px-2 py-1.5">
        Expect banks, government offices and many businesses to be closed — plan errands accordingly. Restaurants and tourist sites usually stay open.
      </div>
    </div>
  );
}

function DestinationPanel() {
  const [state, setState] = useState({ content: null, country: "", city: "", needs_preference: false });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/destination-content")
      .then(({ data }) => setState(data))
      .catch(() => setState({ content: null }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]" data-testid="dest-loading">Loading destination info…</div>;
  }
  if (state.needs_preference) {
    return (
      <div className="aii-card p-8 text-center" data-testid="dest-needs-pref">
        <Globe size={32} className="mx-auto text-[color:var(--aii-muted)] mb-2"/>
        <div className="font-display font-semibold text-lg mb-1">Destination not set yet</div>
        <div className="text-sm text-[color:var(--aii-muted)] max-w-md mx-auto">
          Your Aii Group coordinator hasn&rsquo;t confirmed a destination + city on your booking yet. As soon as that&rsquo;s uploaded from your partner&rsquo;s application, this tab will unlock weather, currency tips, apps, emergency numbers, vaccinations, culture notes and landmarks.
        </div>
      </div>
    );
  }
  const c = state.content;
  const country = state.country || c?.country;
  const city = state.city || c?.city;

  return (
    <div className="space-y-4" data-testid="destination-panel">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Getting to know</div>
        <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><Globe size={22} className="text-brand"/> {city}, {country}</h2>
        <p className="text-sm text-[color:var(--aii-muted)] mt-1">Everything you need to know before you land — updated by your BDOM.</p>
      </div>

      <WeatherCard country={country} city={city}/>

      <HolidaysCard country={country} city={city}/>

      {!c ? (
        <div className="aii-card p-8 text-center" data-testid="dest-empty">
          <Globe size={32} className="mx-auto text-[color:var(--aii-muted)] mb-2"/>
          <div className="text-sm text-[color:var(--aii-muted)]">Your BDOM hasn&rsquo;t published destination info for {city}, {country} yet — check back soon.</div>
        </div>
      ) : (
        <>
          {c.currency && (c.currency.code || c.currency.name) && (
            <div className="aii-card p-5" data-testid="dest-currency">
              <div className="flex items-center gap-2 mb-2">
                <Coins size={16} className="text-brand"/>
                <h3 className="font-display font-semibold text-lg">Currency</h3>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl font-display font-bold text-brand">{c.currency.symbol || "¤"}</span>
                <div>
                  <div className="font-semibold">{c.currency.name || "—"}</div>
                  <div className="text-xs text-[color:var(--aii-muted)] uppercase tracking-wider">{c.currency.code || "—"}</div>
                </div>
              </div>
              {c.currency.tips && <p className="text-sm text-[color:var(--aii-text-2)] leading-relaxed">{c.currency.tips}</p>}
            </div>
          )}

          {c.apps?.length > 0 && (
            <div className="aii-card p-5" data-testid="dest-apps">
              <div className="flex items-center gap-2 mb-3">
                <Smartphone size={16} className="text-brand"/>
                <h3 className="font-display font-semibold text-lg">Handy apps</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {c.apps.map((a, i) => (
                  <a key={`app-${i}-${a.name}`} href={a.url || "#"} target="_blank" rel="noopener noreferrer"
                     data-testid={`dest-app-${i}`}
                     className="flex items-center gap-3 p-3 rounded-lg border border-[color:var(--aii-border)] hover:border-brand hover:bg-[color:var(--aii-green-tint)] transition">
                    <div className="w-10 h-10 rounded-lg bg-[color:var(--aii-green-tint)] text-brand flex items-center justify-center flex-shrink-0">
                      <Smartphone size={18}/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{a.name}</div>
                      <div className="text-[11px] text-[color:var(--aii-muted)] truncate">{a.category || "App"}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {c.emergency_numbers?.length > 0 && (
            <div className="aii-card p-5 border-red-200" data-testid="dest-emergency">
              <div className="flex items-center gap-2 mb-3">
                <PhoneCall size={16} className="text-red-600"/>
                <h3 className="font-display font-semibold text-lg text-red-800">Emergency numbers</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {c.emergency_numbers.map((n, i) => (
                  <a key={`emg-${i}-${n.label}`} href={`tel:${(n.number || "").replace(/[^+\d]/g, "")}`}
                     data-testid={`dest-emg-${i}`}
                     className="border border-red-200 bg-red-50/50 hover:bg-red-100 rounded-lg p-3 text-left transition">
                    <div className="text-[10px] uppercase tracking-widest text-red-700 font-bold">{n.label}</div>
                    <div className="font-display font-bold text-xl text-red-900 mt-1">{n.number}</div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {c.vaccinations?.length > 0 && (
            <div className="aii-card p-5" data-testid="dest-vaccinations">
              <div className="flex items-center gap-2 mb-3">
                <Syringe size={16} className="text-brand"/>
                <h3 className="font-display font-semibold text-lg">Recommended vaccinations</h3>
              </div>
              <ul className="space-y-2">
                {c.vaccinations.map((v, i) => (
                  <li key={`vac-${i}-${v.name}`} className="flex items-start gap-3 pb-2 border-b border-[color:var(--aii-border)] last:border-0"
                      data-testid={`dest-vac-${i}`}>
                    <Syringe size={14} className="text-brand flex-shrink-0 mt-0.5"/>
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{v.name}</div>
                      {v.notes && <div className="text-xs text-[color:var(--aii-text-2)] mt-0.5">{v.notes}</div>}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-start gap-1.5">
                <AlertTriangle size={11} className="flex-shrink-0 mt-0.5"/>
                <span>Consult your local travel clinic 6-8 weeks before departure. This list is a general guide — not medical advice.</span>
              </div>
            </div>
          )}

          {c.culture_shock && (
            <div className="aii-card p-5" data-testid="dest-culture">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={16} className="text-brand"/>
                <h3 className="font-display font-semibold text-lg">Culture shock — heads-up</h3>
              </div>
              <p className="text-sm text-[color:var(--aii-text-2)] leading-relaxed whitespace-pre-wrap">{c.culture_shock}</p>
            </div>
          )}

          {c.landmarks?.length > 0 && (
            <div className="aii-card p-5" data-testid="dest-landmarks">
              <div className="flex items-center gap-2 mb-3">
                <Landmark size={16} className="text-brand"/>
                <h3 className="font-display font-semibold text-lg">Must-see landmarks</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {c.landmarks.map((l, i) => (
                  <div key={`lm-${i}-${l.name}`} className="p-3 border border-[color:var(--aii-border)] rounded-lg" data-testid={`dest-lm-${i}`}>
                    <div className="font-semibold text-sm flex items-center gap-1.5"><MapPin size={12} className="text-brand"/> {l.name}</div>
                    {l.description && <div className="text-xs text-[color:var(--aii-text-2)] mt-1 leading-relaxed">{l.description}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ---------------- Documents (travel docs upload) ----------------
const DOC_ICON = {
  flight_ticket: "🎫",
  passport: "🛂",
  visa: "📘",
  travel_insurance: "🛡️",
  vaccination_card: "💉",
  student_id: "🎓",
};

function DocumentsPanel() {
  const [types, setTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState("");
  const load = () => api.get("/participant/documents").then(({data}) => setRows(data || []));

  useEffect(() => {
    api.get("/participant/document-types").then(({data}) => setTypes(data || [])).catch(() => setTypes([]));
    load();
  }, []);

  const upload = async (docType, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB"); return; }
    setUploading(docType);
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", file);
    try {
      await api.post("/participant/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded — visible to your BDOM");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(""); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/participant/documents/${id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const byType = (t) => rows.filter((r) => r.doc_type === t);
  const fmtSize = (b) => b > 1_000_000 ? `${(b/1_000_000).toFixed(1)} MB` : b > 1000 ? `${Math.round(b/1000)} KB` : `${b} B`;

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Travel wallet</div>
        <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><FileUp size={22} className="text-brand"/> My travel documents</h2>
        <p className="text-sm text-[color:var(--aii-muted)] mt-1">Upload copies of everything you&rsquo;ll need in India — flight ticket, passport, visa, insurance, vaccination card, student ID. Only your BDOM and GEHM can see them.</p>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-4 py-3 text-sm flex items-start gap-2" data-testid="doc-info">
        <ShieldCheck size={18} className="flex-shrink-0 mt-0.5"/>
        <div>
          <div className="font-semibold">Why upload?</div>
          <div className="text-xs mt-0.5">If you lose the original, we can reprint from your uploaded copy on the spot. PDFs and images (JPG/PNG/HEIC) up to 10 MB.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {types.map((t) => {
          const uploaded = byType(t.key);
          return (
            <div key={t.key} className="aii-card p-4" data-testid={`doc-tile-${t.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{DOC_ICON[t.key] || "📄"}</span>
                  <div>
                    <div className="font-display font-semibold text-sm">{t.label}</div>
                    <div className="text-[11px] text-[color:var(--aii-muted)]">{uploaded.length} on file</div>
                  </div>
                </div>
                <label className="cursor-pointer text-xs px-3 py-1.5 bg-brand text-white rounded-md hover:bg-brand-dark flex items-center gap-1 disabled:opacity-50"
                       data-testid={`doc-upload-${t.key}`}>
                  <Upload size={12}/>
                  {uploading === t.key ? "Uploading…" : "Upload"}
                  <input type="file" accept="application/pdf,image/*" className="hidden"
                         disabled={uploading === t.key}
                         onChange={(e) => upload(t.key, e.target.files?.[0])}
                         data-testid={`doc-file-${t.key}`}/>
                </label>
              </div>
              {uploaded.length > 0 && (
                <div className="space-y-1.5 mt-3 pt-3 border-t border-[color:var(--aii-border)]">
                  {uploaded.map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`doc-row-${d.id}`}>
                      <a href={`${API}/files/${d.file_id}?auth=${getToken()}`} target="_blank" rel="noopener noreferrer"
                         className="flex-1 min-w-0 hover:text-brand truncate">
                        <span className="font-medium">{d.filename}</span>
                        <span className="text-[color:var(--aii-muted)]"> · {fmtSize(d.size)}</span>
                      </a>
                      {d.verified && (
                        <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded flex items-center gap-0.5" data-testid={`doc-verified-${d.id}`}>
                          <ShieldCheck size={10}/> Verified
                        </span>
                      )}
                      <button onClick={() => del(d.id)} className="text-red-600 p-1" data-testid={`doc-del-${d.id}`}><Trash2 size={12}/></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---------------- Root layout ----------------
export default function ParticipantPortal() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [me, setMe] = useState(null);
  const [notifPerm, setNotifPerm] = useState(typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const { canInstall, install } = usePwaInstall();
  // Compute a small palette from the participant's booking partner's brand
  // colour so the portal chrome (bg-brand / text-brand / border / pill /
  // hover) re-tints to match the org that recruited them. Falls back to
  // the default Aii green when no partner or no colour is configured.
  const themeStyle = (() => {
    const hex = me?.booking_partner?.brand_color;
    if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return undefined;
    const num = parseInt(hex.slice(1), 16);
    const r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
    const darken = (v) => Math.max(0, Math.round(v * 0.82));
    const tint = (v) => Math.round(v + (255 - v) * 0.92);
    const toHex = (rr, gg, bb) => "#" + [rr, gg, bb].map(x => x.toString(16).padStart(2, "0")).join("");
    return {
      "--aii-green": hex,
      "--aii-green-dark": toHex(darken(r), darken(g), darken(b)),
      "--aii-green-tint": toHex(tint(r), tint(g), tint(b)),
    };
  })();

  useEffect(() => {
    const load = () => api.get("/participant/me").then(({data}) => setMe(data));
    load();
    // Child components (photo upload/delete) fire 'participant:refresh' after
    // mutating the profile — refetch /me so the avatar + banners react.
    window.addEventListener("participant:refresh", load);
    return () => window.removeEventListener("participant:refresh", load);
  }, []);
  useEffect(() => {
    if (!isPushSupported()) return;
    currentSubscription().then((s) => setPushSubscribed(!!s));
  }, [notifPerm]);

  const enableNotifications = async () => {
    if (!isPushSupported()) { toast.error("Notifications not supported on this device"); return; }
    try {
      await enableWebPush();
      setNotifPerm("granted");
      setPushSubscribed(true);
      toast.success("Background push enabled — even when the app is closed, you'll get updates.");
    } catch (e) {
      toast.error(e?.message || "Could not enable notifications");
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--aii-bg)]" style={themeStyle}>
      <FirstLoginNotifPrompt userId={user?.id}/>
      <header className="bg-white border-b border-[color:var(--aii-border)] sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {/* Whitelabel: participants recruited by a partner see that
                partner's logo + org name in the header. Everyone else
                keeps the Aii Group mark. */}
            {me?.booking_partner?.logo_id ? (
              <img src={`${API}/files/${me.booking_partner.logo_id}?auth=${getToken()}`}
                   alt={me.booking_partner.org_name || "Partner"}
                   className="w-9 h-9 object-contain rounded"
                   onError={(e) => { e.currentTarget.src = "/aii-logo.png"; }}/>
            ) : (
              <img src="/aii-logo.png" alt="Aii Group" className="w-9 h-9 object-contain"/>
            )}
            <div className="min-w-0">
              <div className="font-display text-sm font-bold truncate" data-testid="portal-brand-name">
                {me?.booking_partner?.org_name || <>AII GROUP<span className="text-brand">®</span></>}
              </div>
              <div className="text-[10px] text-[color:var(--aii-muted)]">Hi {user.name.split(" ")[0]}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canInstall && (
              <button onClick={install} data-testid="install-app-btn" title="Install as an app on this device"
                className="text-xs px-2.5 py-1.5 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1 shadow">
                <Download size={12}/> <span className="hidden sm:inline">Install app</span>
              </button>
            )}
            {notifPerm !== "granted" || !pushSubscribed ? (
              <button onClick={enableNotifications} data-testid="enable-notif-btn" title="Enable push notifications"
                className="text-xs px-2.5 py-1.5 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
                <Bell size={12}/> <span className="hidden sm:inline">Enable alerts</span>
              </button>
            ) : (
              <span data-testid="notif-on-badge" className="text-xs px-2.5 py-1.5 bg-emerald-50 text-emerald-700 rounded flex items-center gap-1"><Bell size={12}/><span className="hidden sm:inline">Alerts on</span></span>
            )}
            <button onClick={logout} data-testid="logout-btn" className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1"><LogOut size={12}/> <span className="hidden sm:inline">Sign out</span></button>
          </div>
        </div>
        <nav className="max-w-4xl mx-auto px-2 pb-2 overflow-x-auto flex gap-1">
          {tabsForPlacement(me?.is_indian_placement).map(({key,label,icon:Icon}) => (
            <button key={key} onClick={()=>setTab(key)} data-testid={`ptab-${key}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap ${tab === key ? "bg-brand text-white" : "text-[color:var(--aii-text-2)] hover:bg-[color:var(--aii-green-tint)]"}`}>
              <Icon size={12}/> {label}
            </button>
          ))}
        </nav>
      </header>
      <main className="max-w-4xl mx-auto p-4">
        {(() => {
          // Farewell banner — surfaces at the top of every tab when the
          // participant is leaving within 48h so the survey CTA is
          // impossible to miss. Auto-hides once they cross the departure
          // date. `me?.booking?.end_date` is the source of truth.
          const end = me?.booking?.end_date;
          if (!end) return null;
          const today = new Date(); today.setHours(0,0,0,0);
          const endDate = new Date(end.slice(0,10) + "T00:00:00");
          const days = Math.round((endDate - today) / 86400000);
          if (days < 0 || days > 2) return null;
          if (tab === "feedback") return null;  // hide when already there
          const label = days === 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;
          return (
            <div
              data-testid="farewell-banner"
              className="mb-4 rounded-lg border-2 border-brand bg-[color:var(--aii-green-tint)] p-4 flex flex-wrap items-center gap-3">
              <div className="text-2xl flex-shrink-0">🎁</div>
              <div className="flex-1 min-w-[220px]">
                <div className="font-display font-semibold text-[color:var(--aii-text-1)]">
                  You&apos;re flying home {label}!
                </div>
                <div className="text-xs text-[color:var(--aii-text-2)] mt-0.5">
                  Leave a short farewell note about your {me?.booking?.destination || "trip"} experience — the team + your buddies read every one.
                </div>
              </div>
              <button
                data-testid="farewell-cta"
                onClick={() => setTab("feedback")}
                className="text-sm px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5 flex-shrink-0">
                <MessageSquare size={14}/> Leave farewell note
              </button>
            </div>
          );
        })()}
        {tab === "dashboard" && <DashboardPanel me={me} onGoto={setTab}/>}
        {tab === "cform" && <CFormPanel/>}
        {tab === "general" && <GeneralInfoPanel/>}
        {tab === "documents" && <DocumentsPanel/>}
        {tab === "destination" && <DestinationPanel/>}
        {tab === "services" && <ServicesPanel/>}
        {tab === "activities" && <ActivitiesTabPanel/>}
        {tab === "projects" && <ThreadPanel kind="projects" title="Projects & Placement Suggestions" targetRole="PCWM"/>}
        {tab === "instructions" && <InstructionsPanel/>}
        {tab === "videos" && <VideosPanel/>}
        {tab === "attendance" && <AttendancePanel user={user}/>}
        {tab === "feedback" && (
          <div className="max-w-2xl">
            <ParticipantFeedbackCard />
          </div>
        )}
        {tab === "contacts" && <ContactsPanel/>}
        {tab === "suggestions" && <ThreadPanel kind="feedback" title="Suggestions" targetRole="BDOM & Founder" placeholder="What could we do better? Ideas, tweaks, wishlist — everything welcome." submitLabel="Share suggestion"/>}
        {tab === "disclaimer" && <DisclaimerPanel/>}
      </main>
    </div>
  );
}
